import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue, set, get } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { database, storage } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useCursor } from '../contexts/CursorContext';
import './Intimacy.css';

const Intimacy = () => {
  const { userProfile } = useAuth();
  const { setPageOverride } = useCursor();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [selectedMode, setSelectedMode] = useState(null); // 'eros' or 'freaky'
  const [selectedFreakyMode, setSelectedFreakyMode] = useState(null); // 'command', 'breathControl', 'carnival'
  const [partnerMode, setPartnerMode] = useState(null);
  const [intimacyPassword, setIntimacyPassword] = useState('');
  const [protocolAccepted, setProtocolAccepted] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [acceptedProtocols, setAcceptedProtocols] = useState([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    points: 10,
    timeframe: 24,
    penaltyPoints: 5,
    uploadType: 'none',
    bonusChallenge: '',
    bonusPoints: 0
  });
  const [tasks, setTasks] = useState([]);
  const [complianceItems, setComplianceItems] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showTaskDetailModal, setShowTaskDetailModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadPreviews, setUploadPreviews] = useState([]);
  const [textSubmission, setTextSubmission] = useState('');
  const [favorMeter, setFavorMeter] = useState(50); // 0-100
  const [isUploading, setIsUploading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [expandedVideo, setExpandedVideo] = useState(null);
  const [expandedAudio, setExpandedAudio] = useState(null);
  const [awardBonus, setAwardBonus] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archivedTasks, setArchivedTasks] = useState([]);
  const [showRewardVault, setShowRewardVault] = useState(false);
  const [vaultContents, setVaultContents] = useState([]);
  const [rewardStorage, setRewardStorage] = useState([]);
  const [showAddVaultContent, setShowAddVaultContent] = useState(false);
  const [newVaultContent, setNewVaultContent] = useState({
    type: 'text', // 'text', 'image', 'video', 'audio'
    title: '',
    content: '', // text content or URL for media
    mediaType: '', // mime type for media
    file: null // file object for upload
  });
  const [showProtocolEditor, setShowProtocolEditor] = useState(false);
  const [editingProtocols, setEditingProtocols] = useState([]);
  const [showRewardStorage, setShowRewardStorage] = useState(false);
  const [expandedReward, setExpandedReward] = useState(null);

  // Punishment System States - use sessionStorage so dismissal persists across pages
  const [punishments, setPunishments] = useState([]); // Pool of available punishments
  const [activePunishment, setActivePunishment] = useState(null); // Current active punishment
  const [showPunishmentPopup, setShowPunishmentPopup] = useState(false); // Popup for Omi when punished
  const [punishmentPopupDismissed, setPunishmentPopupDismissed] = useState(() => {
    return sessionStorage.getItem('punishmentPopupDismissed') === 'true';
  }); // Track if popup dismissed this session
  const [showPunishmentManager, setShowPunishmentManager] = useState(false); // Henry's punishment management modal
  const [newPunishmentText, setNewPunishmentText] = useState(''); // For adding new punishments

  // Tribute Archive States (Henry only)
  const [showTributeArchive, setShowTributeArchive] = useState(false);
  const [tributes, setTributes] = useState([]);
  const [expandedTribute, setExpandedTribute] = useState(null);

  // Refs for file inputs to allow resetting
  const fileInputRef = useRef(null);
  const vaultFileInputRef = useRef(null);

  // Eros Mode States
  const [showPoetry, setShowPoetry] = useState(false);
  const [poems, setPoems] = useState([]);
  const [selectedPoem, setSelectedPoem] = useState(null);
  const [showAddPoem, setShowAddPoem] = useState(false);
  const [newPoem, setNewPoem] = useState({ title: '', content: '', author: '' });
  const [showWhisperVault, setShowWhisperVault] = useState(false);
  const [whispers, setWhispers] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [mediaStream, setMediaStream] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [newWhisperTitle, setNewWhisperTitle] = useState('');
  const [playingWhisper, setPlayingWhisper] = useState(null);

  // ============================================
  // Choke Mode States
  // ============================================

  // Breath Controller (Henry controls breathing pattern)
  const [breathSettings, setBreathSettings] = useState({
    inhale: 4,
    hold: 4,
    exhale: 4,
    intensity: 'gentle' // gentle, moderate, intense
  });
  const [breathPhase, setBreathPhase] = useState('idle'); // idle, inhale, hold, exhale
  const [breathActive, setBreathActive] = useState(false);
  const [surpriseHold, setSurpriseHold] = useState(false);

  // Permission to Breathe
  const [breathRequest, setBreathRequest] = useState(null); // { timestamp, status: 'pending' | 'granted' | 'denied' }
  const [partnerBreathRequest, setPartnerBreathRequest] = useState(null);

  // Countdown Cage
  const [cageActive, setCageActive] = useState(false);
  const [cageTimer, setCageTimer] = useState(0);
  const [cageMaxTime, setCageMaxTime] = useState(30);
  const [cageSafeSignal, setCageSafeSignal] = useState(false);
  const [partnerCageState, setPartnerCageState] = useState(null);

  // Whispered Commands
  const [chokeWhispers, setChokeWhispers] = useState([]);
  const [isRecordingWhisper, setIsRecordingWhisper] = useState(false);
  const [whisperRecorder, setWhisperRecorder] = useState(null);
  const [whisperStream, setWhisperStream] = useState(null);
  const [whisperChunks, setWhisperChunks] = useState([]);
  const [playingCommand, setPlayingCommand] = useState(null);

  // Constriction Meter (tied to favor, visual feedback)
  const [constrictionLevel, setConstrictionLevel] = useState(50); // 0-100, lower = tighter

  // Breath Sync Challenge
  const [syncActive, setSyncActive] = useState(false);
  const [syncTaps, setSyncTaps] = useState([]);
  const [partnerSyncTaps, setPartnerSyncTaps] = useState([]);
  const [syncScore, setSyncScore] = useState(0);

  // Edge Timer (Henry sets secret duration)
  const [edgeActive, setEdgeActive] = useState(false);
  const [edgeSecretDuration, setEdgeSecretDuration] = useState(60);
  const [edgeStartTime, setEdgeStartTime] = useState(null);
  const [edgeProgress, setEdgeProgress] = useState(0);
  const [edgeCompleted, setEdgeCompleted] = useState(false);
  const [edgeRewardMessage, setEdgeRewardMessage] = useState('');

  // Gasp Button
  const [gaspCount, setGaspCount] = useState(0);
  const [gaspLog, setGaspLog] = useState([]);
  const [showGaspLog, setShowGaspLog] = useState(false);

  // Choke mode audio
  const [heartbeatAudio, setHeartbeatAudio] = useState(null);
  const [chokeAudioEnabled, setChokeAudioEnabled] = useState(true);

  // Override global custom cursor on this page - always use default
  useEffect(() => {
    setPageOverride(true);
    return () => setPageOverride(false);
  }, [setPageOverride]);

  // Handle return from Eros Poetry page - skip auth and go directly to Eros mode
  useEffect(() => {
    if (location.state?.returnToEros) {
      setIsAuthenticated(true);
      setSelectedMode('eros');
      // Clear the state so refresh doesn't bypass auth
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Track fullscreen state
  useEffect(() => {
    const checkFullscreen = () => {
      const isInFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isInFullscreen);
    };

    // Check initial state
    checkFullscreen();

    // Listen for changes
    document.addEventListener('fullscreenchange', checkFullscreen);
    document.addEventListener('webkitfullscreenchange', checkFullscreen);
    document.addEventListener('mozfullscreenchange', checkFullscreen);
    document.addEventListener('MSFullscreenChange', checkFullscreen);

    return () => {
      document.removeEventListener('fullscreenchange', checkFullscreen);
      document.removeEventListener('webkitfullscreenchange', checkFullscreen);
      document.removeEventListener('mozfullscreenchange', checkFullscreen);
      document.removeEventListener('MSFullscreenChange', checkFullscreen);
    };
  }, []);

  // Set upload mode flag for FullscreenHandler - allow grace period when uploading
  // Omi: when uploading task proof
  // Henry: when adding media to vault
  useEffect(() => {
    const isOmiInUploadMode = userProfile?.name === 'Omi' &&
                              selectedTask?.status === 'accepted' &&
                              showTaskDetailModal;
    const isHenryInVaultUploadMode = userProfile?.name === 'Henry' &&
                                      showRewardVault &&
                                      showAddVaultContent;
    window.__intimacyUploadMode = isOmiInUploadMode || isHenryInVaultUploadMode;

    return () => {
      window.__intimacyUploadMode = false;
    };
  }, [userProfile?.name, selectedTask?.status, showTaskDetailModal, showRewardVault, showAddVaultContent]);

  // Load intimacy password from Firebase
  useEffect(() => {
    const passwordRef = ref(database, 'settings/intimacyPassword');
    const unsubscribe = onValue(passwordRef, (snapshot) => {
      if (snapshot.exists()) {
        setIntimacyPassword(snapshot.val());
      } else {
        // Default password if not set
        setIntimacyPassword('intimate');
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen for partner's mode selection
  useEffect(() => {
    if (!isAuthenticated) return;

    const partnerName = userProfile?.name === 'Omi' ? 'Henry' : 'Omi';
    const partnerModeRef = ref(database, `intimacy/currentMode/${partnerName}`);

    const unsubscribe = onValue(partnerModeRef, (snapshot) => {
      if (snapshot.exists()) {
        setPartnerMode(snapshot.val());
      } else {
        setPartnerMode(null);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, userProfile]);

  // Load protocols from Firebase
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const protocolsRef = ref(database, 'intimacy/freaky/protocols');
    const unsubscribe = onValue(protocolsRef, (snapshot) => {
      if (snapshot.exists()) {
        const protocolsData = snapshot.val();
        const protocolsArray = Object.entries(protocolsData).map(([id, protocol]) => ({
          id,
          ...protocol
        }));
        setProtocols(protocolsArray);
      } else {
        // Default protocols if none exist
        setProtocols([
          { id: '1', text: 'I will complete tasks within the given timeframe' },
          { id: '2', text: 'I will submit honest proof of completion' },
          { id: '3', text: 'I understand that failure results in meter loss' },
          { id: '4', text: 'I acknowledge the authority of the dashboard' }
        ]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Check if protocol has been accepted today
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || userProfile?.name !== 'Omi') return;

    const today = new Date().toISOString().split('T')[0];
    const protocolStatusRef = ref(database, `intimacy/freaky/protocolStatus/${userProfile?.name}/${today}`);

    const unsubscribe = onValue(protocolStatusRef, (snapshot) => {
      if (snapshot.exists()) {
        setProtocolAccepted(true);
      } else {
        setProtocolAccepted(false);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, userProfile]);

  // Load tasks from Firebase
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const tasksRef = ref(database, 'intimacy/freaky/tasks');
    const unsubscribe = onValue(tasksRef, (snapshot) => {
      if (snapshot.exists()) {
        const tasksData = snapshot.val();
        const tasksArray = Object.entries(tasksData).map(([id, task]) => ({
          id,
          ...task
        }));
        setTasks(tasksArray);
      } else {
        setTasks([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Load archived tasks
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const archiveRef = ref(database, 'intimacy/freaky/archivedTasks');
    const unsubscribe = onValue(archiveRef, (snapshot) => {
      if (snapshot.exists()) {
        const archiveData = snapshot.val();
        const archiveArray = Object.entries(archiveData).map(([id, task]) => ({
          id,
          ...task
        }));
        // Sort by archived date, newest first
        archiveArray.sort((a, b) => new Date(b.archivedAt) - new Date(a.archivedAt));
        setArchivedTasks(archiveArray);
      } else {
        setArchivedTasks([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Auto-expire accepted tasks past their deadline
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const checkExpiredTasks = async () => {
      const now = new Date();
      const expiredTasks = tasks.filter(t =>
        t.status === 'accepted' && t.deadline && new Date(t.deadline) < now
      );

      if (expiredTasks.length === 0) return;

      const meterRef = ref(database, 'intimacy/freaky/favorMeter');
      const punishmentRef = ref(database, 'intimacy/freaky/activePunishment');

      for (const task of expiredTasks) {
        const taskRef = ref(database, `intimacy/freaky/tasks/${task.id}`);
        const penaltyAmount = task.points || 10;

        // Read current meter and punishment state fresh from Firebase
        const [meterSnap, punishmentSnap] = await Promise.all([
          get(meterRef),
          get(punishmentRef)
        ]);
        const currentMeter = meterSnap.val() ?? 50;
        const currentPunishment = punishmentSnap.val();
        const newMeter = currentMeter - penaltyAmount;

        if (newMeter < 0 && !currentPunishment) {
          await activatePunishmentProtocol(penaltyAmount);
        } else {
          await set(meterRef, Math.max(0, newMeter));
        }

        await set(taskRef, {
          ...task,
          status: 'expired',
          expiredAt: now.toISOString()
        });
      }
    };

    checkExpiredTasks();
    const interval = setInterval(checkExpiredTasks, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, selectedMode, tasks]);

  // Load vault contents from Firebase (what Henry adds)
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const vaultRef = ref(database, 'intimacy/freaky/vaultContents');
    const unsubscribe = onValue(vaultRef, (snapshot) => {
      if (snapshot.exists()) {
        const vaultData = snapshot.val();
        const vaultArray = Object.entries(vaultData).map(([id, item]) => ({
          id,
          ...item
        }));
        setVaultContents(vaultArray);
      } else {
        setVaultContents([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Load reward storage from Firebase (what Omi has earned)
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const storageRef = ref(database, 'intimacy/freaky/rewardStorage');
    const unsubscribe = onValue(storageRef, (snapshot) => {
      if (snapshot.exists()) {
        const storageData = snapshot.val();
        const storageArray = Object.entries(storageData).map(([id, item]) => ({
          id,
          ...item
        }));
        setRewardStorage(storageArray);
      } else {
        setRewardStorage([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Load compliance wall items
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const complianceRef = ref(database, 'intimacy/freaky/compliance');
    const unsubscribe = onValue(complianceRef, (snapshot) => {
      if (snapshot.exists()) {
        const complianceData = snapshot.val();
        const complianceArray = Object.entries(complianceData).map(([id, item]) => ({
          id,
          ...item
        }));
        setComplianceItems(complianceArray);
      } else {
        setComplianceItems([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Load favor meter
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const meterRef = ref(database, 'intimacy/freaky/favorMeter');
    const unsubscribe = onValue(meterRef, (snapshot) => {
      if (snapshot.exists()) {
        setFavorMeter(snapshot.val());
      } else {
        // Initialize at 50%
        set(ref(database, 'intimacy/freaky/favorMeter'), 50);
        setFavorMeter(50);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Load Tribute Archive from Firebase (Henry only)
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const tributeRef = ref(database, 'intimacy/freaky/tributes');
    const unsubscribe = onValue(tributeRef, (snapshot) => {
      if (snapshot.exists()) {
        const tributeData = snapshot.val();
        const tributeArray = Object.entries(tributeData).map(([id, item]) => ({
          id,
          ...item
        }));
        // Sort by date, newest first
        tributeArray.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
        setTributes(tributeArray);
      } else {
        setTributes([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Load punishment pool from Firebase
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const punishmentsRef = ref(database, 'intimacy/freaky/punishments');
    const unsubscribe = onValue(punishmentsRef, (snapshot) => {
      if (snapshot.exists()) {
        const punishmentsData = snapshot.val();
        const punishmentsArray = Object.entries(punishmentsData).map(([id, punishment]) => ({
          id,
          ...punishment
        }));
        setPunishments(punishmentsArray);
      } else {
        setPunishments([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Load active punishment status from Firebase
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky') return;

    const activePunishmentRef = ref(database, 'intimacy/freaky/activePunishment');
    const unsubscribe = onValue(activePunishmentRef, (snapshot) => {
      if (snapshot.exists()) {
        setActivePunishment(snapshot.val());
        // Show popup for Omi if punishment is active and they haven't dismissed it this session
        if (userProfile?.name === 'Omi' && !punishmentPopupDismissed) {
          setShowPunishmentPopup(true);
        }
      } else {
        setActivePunishment(null);
        setShowPunishmentPopup(false);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, userProfile, punishmentPopupDismissed]);

  // Load Eros poems from Firebase
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'eros') return;

    const poemsRef = ref(database, 'intimacy/eros/poems');
    const unsubscribe = onValue(poemsRef, (snapshot) => {
      if (snapshot.exists()) {
        const poemsData = snapshot.val();
        const poemsArray = Object.entries(poemsData).map(([id, poem]) => ({
          id,
          ...poem
        }));
        // Sort by date, newest first
        poemsArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setPoems(poemsArray);
      } else {
        setPoems([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // Load Eros whispers from Firebase
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'eros') return;

    const whispersRef = ref(database, 'intimacy/eros/whispers');
    const unsubscribe = onValue(whispersRef, (snapshot) => {
      if (snapshot.exists()) {
        const whispersData = snapshot.val();
        const whispersArray = Object.entries(whispersData).map(([id, whisper]) => ({
          id,
          ...whisper
        }));
        // Sort by date, newest first
        whispersArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setWhispers(whispersArray);
      } else {
        setWhispers([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode]);

  // ============================================
  // Choke Mode Firebase Listeners
  // ============================================

  // Load breath settings from Firebase
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const breathRef = ref(database, 'intimacy/freaky/choke/breathSettings');
    const unsubscribe = onValue(breathRef, (snapshot) => {
      if (snapshot.exists()) {
        setBreathSettings(snapshot.val());
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode]);

  // Load breath phase from Firebase (real-time sync)
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const phaseRef = ref(database, 'intimacy/freaky/choke/breathPhase');
    const unsubscribe = onValue(phaseRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setBreathPhase(data.phase || 'idle');
        setBreathActive(data.active || false);
        setSurpriseHold(data.surpriseHold || false);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode]);

  // Load breath request status (Permission to Breathe)
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const partnerName = userProfile?.name === 'Omi' ? 'Henry' : 'Omi';
    const requestRef = ref(database, `intimacy/freaky/choke/breathRequests/${partnerName}`);
    const unsubscribe = onValue(requestRef, (snapshot) => {
      if (snapshot.exists()) {
        setPartnerBreathRequest(snapshot.val());
      } else {
        setPartnerBreathRequest(null);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode, userProfile]);

  // Load own breath request status
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const requestRef = ref(database, `intimacy/freaky/choke/breathRequests/${userProfile?.name}`);
    const unsubscribe = onValue(requestRef, (snapshot) => {
      if (snapshot.exists()) {
        setBreathRequest(snapshot.val());
      } else {
        setBreathRequest(null);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode, userProfile]);

  // Load countdown cage state
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const cageRef = ref(database, 'intimacy/freaky/choke/cage');
    const unsubscribe = onValue(cageRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setPartnerCageState(data);
        if (data.active && data.startedBy !== userProfile?.name) {
          setCageActive(true);
          setCageMaxTime(data.maxTime || 30);
        }
      } else {
        setPartnerCageState(null);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode, userProfile]);

  // Load whispered commands
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const whispersRef = ref(database, 'intimacy/freaky/choke/whisperCommands');
    const unsubscribe = onValue(whispersRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const whispersArray = Object.entries(data).map(([id, whisper]) => ({
          id,
          ...whisper
        }));
        whispersArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setChokeWhispers(whispersArray);
      } else {
        setChokeWhispers([]);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode]);

  // Load constriction level (tied to favor meter)
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;
    // Constriction is inverse of favor - lower favor = tighter constriction
    setConstrictionLevel(100 - favorMeter);
  }, [isAuthenticated, selectedMode, selectedFreakyMode, favorMeter]);

  // Load sync challenge state
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const syncRef = ref(database, 'intimacy/freaky/choke/syncChallenge');
    const unsubscribe = onValue(syncRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setSyncActive(data.active || false);
        setSyncScore(data.score || 0);
        const partnerName = userProfile?.name === 'Omi' ? 'Henry' : 'Omi';
        setPartnerSyncTaps(data.taps?.[partnerName] || []);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode, userProfile]);

  // Load edge timer state
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const edgeRef = ref(database, 'intimacy/freaky/choke/edgeTimer');
    const unsubscribe = onValue(edgeRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setEdgeActive(data.active || false);
        if (userProfile?.name === 'Henry') {
          setEdgeSecretDuration(data.duration || 60);
        }
        setEdgeStartTime(data.startTime || null);
        setEdgeRewardMessage(data.rewardMessage || '');
        setEdgeCompleted(data.completed || false);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode, userProfile]);

  // Load gasp log
  useEffect(() => {
    if (!isAuthenticated || selectedMode !== 'freaky' || selectedFreakyMode !== 'breathControl') return;

    const gaspRef = ref(database, 'intimacy/freaky/choke/gaspLog');
    const unsubscribe = onValue(gaspRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const gaspArray = Object.entries(data).map(([id, gasp]) => ({
          id,
          ...gasp
        }));
        gaspArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setGaspLog(gaspArray);
        // Count today's gasps
        const today = new Date().toDateString();
        const todayGasps = gaspArray.filter(g => new Date(g.timestamp).toDateString() === today);
        setGaspCount(todayGasps.length);
      } else {
        setGaspLog([]);
        setGaspCount(0);
      }
    });

    return () => unsubscribe();
  }, [isAuthenticated, selectedMode, selectedFreakyMode]);

  // Edge timer progress tracker
  useEffect(() => {
    if (!edgeActive || !edgeStartTime || userProfile?.name === 'Henry') return;

    const interval = setInterval(() => {
      const elapsed = (Date.now() - edgeStartTime) / 1000;
      // Omi doesn't know the duration, so we calculate a fake progress that accelerates
      // This creates uncertainty about when it will end
      const fakeProgress = Math.min(100, (elapsed / 120) * 100 * (1 + Math.random() * 0.1));
      setEdgeProgress(fakeProgress);
    }, 500);

    return () => clearInterval(interval);
  }, [edgeActive, edgeStartTime, userProfile]);

  // Breath cycle controller
  useEffect(() => {
    if (!breathActive || userProfile?.name !== 'Henry') return;

    const runBreathCycle = async () => {
      // Inhale
      await set(ref(database, 'intimacy/freaky/choke/breathPhase'), {
        phase: 'inhale',
        active: true,
        surpriseHold: false
      });
      await new Promise(r => setTimeout(r, breathSettings.inhale * 1000));

      // Hold
      await set(ref(database, 'intimacy/freaky/choke/breathPhase'), {
        phase: 'hold',
        active: true,
        surpriseHold: false
      });
      await new Promise(r => setTimeout(r, breathSettings.hold * 1000));

      // Exhale
      await set(ref(database, 'intimacy/freaky/choke/breathPhase'), {
        phase: 'exhale',
        active: true,
        surpriseHold: false
      });
      await new Promise(r => setTimeout(r, breathSettings.exhale * 1000));
    };

    const cycleInterval = setInterval(() => {
      if (breathActive) {
        runBreathCycle();
      }
    }, (breathSettings.inhale + breathSettings.hold + breathSettings.exhale) * 1000);

    // Start first cycle immediately
    runBreathCycle();

    return () => clearInterval(cycleInterval);
  }, [breathActive, breathSettings, userProfile]);

  // Cage timer countdown
  useEffect(() => {
    if (!cageActive) return;

    const interval = setInterval(() => {
      setCageTimer(prev => {
        const newTime = prev + 1;
        if (newTime >= cageMaxTime) {
          // Timer completed - auto release
          setCageActive(false);
          set(ref(database, 'intimacy/freaky/choke/cage'), {
            active: false,
            completedAt: Date.now(),
            completedBy: 'timer'
          });
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [cageActive, cageMaxTime]);

  // Edge timer completion check (Henry side monitors for completion)
  useEffect(() => {
    if (!edgeActive || !edgeStartTime || userProfile?.name !== 'Henry') return;

    const interval = setInterval(async () => {
      const elapsed = (Date.now() - edgeStartTime) / 1000;
      if (elapsed >= edgeSecretDuration && !edgeCompleted) {
        await handleCompleteEdge();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [edgeActive, edgeStartTime, edgeSecretDuration, edgeCompleted, userProfile]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    if (password === intimacyPassword) {
      setIsAuthenticated(true);
      setError('');
      setPassword('');
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };

  const handleModeSelect = async (mode) => {
    setSelectedMode(mode);

    // Save mode to Firebase
    const userModeRef = ref(database, `intimacy/currentMode/${userProfile?.name}`);
    await set(userModeRef, mode);

    // Henry bypasses protocol list in Freaky mode
    if (mode === 'freaky' && userProfile?.name === 'Henry') {
      setProtocolAccepted(true);
    }
  };


  const handleProtocolAccept = (protocolId) => {
    if (!acceptedProtocols.includes(protocolId)) {
      setAcceptedProtocols([...acceptedProtocols, protocolId]);
    }
  };

  const handleProtocolSubmit = async () => {
    if (acceptedProtocols.length !== protocols.length) return;

    const today = new Date().toISOString().split('T')[0];
    const protocolStatusRef = ref(database, `intimacy/freaky/protocolStatus/${userProfile?.name}/${today}`);
    await set(protocolStatusRef, {
      accepted: true,
      timestamp: new Date().toISOString(),
      protocolIds: acceptedProtocols
    });

    setProtocolAccepted(true);
  };

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.description) return;

    const taskId = Date.now().toString();
    const taskRef = ref(database, `intimacy/freaky/tasks/${taskId}`);
    await set(taskRef, {
      ...newTask,
      createdBy: userProfile?.name,
      createdAt: new Date().toISOString(),
      status: 'available'
    });

    setNewTask({
      title: '',
      description: '',
      points: 10,
      timeframe: 24,
      penaltyPoints: 5,
      uploadType: 'none',
      bonusChallenge: '',
      bonusPoints: 0
    });
    setShowTaskModal(false);
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setShowTaskDetailModal(true);
  };

  // Activate punishment protocol when favor would go below 0
  const activatePunishmentProtocol = async (penaltyAmount) => {
    // Only activate if there's no active punishment and punishments exist in the pool
    if (activePunishment || punishments.length === 0) {
      // Just set meter to 0 if punishment can't be activated
      const meterRef = ref(database, 'intimacy/freaky/favorMeter');
      await set(meterRef, 0);
      return;
    }

    // Select a random punishment from the pool
    const randomIndex = Math.floor(Math.random() * punishments.length);
    const selectedPunishment = punishments[randomIndex];

    // Activate the punishment
    const activePunishmentRef = ref(database, 'intimacy/freaky/activePunishment');
    await set(activePunishmentRef, {
      ...selectedPunishment,
      activatedAt: new Date().toISOString(),
      triggerPenalty: penaltyAmount
    });

    // Set favor meter to 0
    const meterRef = ref(database, 'intimacy/freaky/favorMeter');
    await set(meterRef, 0);
  };

  // Complete punishment and restore access (Henry only)
  const handleCompletePunishment = async () => {
    if (!activePunishment) return;

    // Clear active punishment
    const activePunishmentRef = ref(database, 'intimacy/freaky/activePunishment');
    await set(activePunishmentRef, null);

    // Reset favor meter to 0 (in case it's negative somehow)
    const meterRef = ref(database, 'intimacy/freaky/favorMeter');
    await set(meterRef, 0);

    // Clear popup dismissed state (both local and sessionStorage for next punishment)
    setPunishmentPopupDismissed(false);
    sessionStorage.removeItem('punishmentPopupDismissed');
  };

  // Add a new punishment to the pool (Henry only)
  const handleAddPunishment = async () => {
    if (!newPunishmentText.trim()) return;

    const punishmentId = Date.now().toString();
    const punishmentRef = ref(database, `intimacy/freaky/punishments/${punishmentId}`);
    await set(punishmentRef, {
      text: newPunishmentText.trim(),
      createdAt: new Date().toISOString(),
      createdBy: userProfile?.name
    });

    setNewPunishmentText('');
  };

  // Delete a punishment from the pool (Henry only)
  const handleDeletePunishment = async (punishmentId) => {
    const punishmentRef = ref(database, `intimacy/freaky/punishments/${punishmentId}`);
    await set(punishmentRef, null);
  };

  // ============================================
  // Choke Mode Handler Functions
  // ============================================

  // Start/Stop breath control (Henry only)
  const handleToggleBreathControl = async () => {
    const newActive = !breathActive;
    await set(ref(database, 'intimacy/freaky/choke/breathPhase'), {
      phase: newActive ? 'inhale' : 'idle',
      active: newActive,
      surpriseHold: false
    });
  };

  // Update breath settings (Henry only)
  const handleUpdateBreathSettings = async (newSettings) => {
    await set(ref(database, 'intimacy/freaky/choke/breathSettings'), newSettings);
    setBreathSettings(newSettings);
  };

  // Trigger surprise hold (Henry only)
  const handleSurpriseHold = async () => {
    await set(ref(database, 'intimacy/freaky/choke/breathPhase'), {
      phase: 'hold',
      active: true,
      surpriseHold: true
    });
  };

  // Request permission to breathe (Omi only)
  const handleRequestBreath = async () => {
    await set(ref(database, `intimacy/freaky/choke/breathRequests/${userProfile?.name}`), {
      status: 'pending',
      timestamp: Date.now()
    });
  };

  // Grant/Deny breath request (Henry only)
  const handleRespondToBreathRequest = async (granted) => {
    const partnerName = userProfile?.name === 'Omi' ? 'Henry' : 'Omi';
    await set(ref(database, `intimacy/freaky/choke/breathRequests/${partnerName}`), {
      status: granted ? 'granted' : 'denied',
      timestamp: Date.now(),
      respondedAt: Date.now()
    });

    // Clear after 3 seconds
    setTimeout(async () => {
      await set(ref(database, `intimacy/freaky/choke/breathRequests/${partnerName}`), null);
    }, 3000);
  };

  // Start countdown cage (Omi sets timer)
  const handleStartCage = async (duration) => {
    setCageActive(true);
    setCageTimer(0);
    setCageMaxTime(duration);
    await set(ref(database, 'intimacy/freaky/choke/cage'), {
      active: true,
      startedBy: userProfile?.name,
      startTime: Date.now(),
      maxTime: duration
    });
  };

  // Release cage early (Henry only)
  const handleReleaseCage = async () => {
    await set(ref(database, 'intimacy/freaky/choke/cage'), {
      active: false,
      releasedBy: 'Henry',
      releasedAt: Date.now()
    });
    setCageActive(false);
    setCageTimer(0);
  };

  // Safe signal (Omi only - emergency exit)
  const handleSafeSignal = async () => {
    setCageSafeSignal(true);
    await set(ref(database, 'intimacy/freaky/choke/cage'), {
      active: false,
      safeSignal: true,
      signalBy: userProfile?.name,
      signalAt: Date.now()
    });
    setCageActive(false);
    setCageTimer(0);
  };

  // Record whispered command (Henry only)
  const handleStartWhisperRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = async () => {
          const whisperData = reader.result;
          const whisperId = Date.now().toString();
          await set(ref(database, `intimacy/freaky/choke/whisperCommands/${whisperId}`), {
            audio: whisperData,
            createdAt: new Date().toISOString(),
            createdBy: userProfile?.name
          });
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      setWhisperRecorder(recorder);
      setWhisperStream(stream);
      setWhisperChunks([]);
      recorder.start();
      setIsRecordingWhisper(true);
    } catch (error) {
      console.error('Error starting whisper recording:', error);
    }
  };

  const handleStopWhisperRecording = () => {
    if (whisperRecorder && whisperRecorder.state !== 'inactive') {
      whisperRecorder.stop();
      setIsRecordingWhisper(false);
    }
  };

  // Delete whispered command (Henry only)
  const handleDeleteWhisper = async (whisperId) => {
    await set(ref(database, `intimacy/freaky/choke/whisperCommands/${whisperId}`), null);
  };

  // Play random whispered command
  const handlePlayRandomWhisper = () => {
    if (chokeWhispers.length === 0) return;
    const randomIndex = Math.floor(Math.random() * chokeWhispers.length);
    const whisper = chokeWhispers[randomIndex];
    setPlayingCommand(whisper);
    const audio = new Audio(whisper.audio);
    audio.onended = () => setPlayingCommand(null);
    audio.play();
  };

  // Start breath sync challenge
  const handleStartSyncChallenge = async () => {
    await set(ref(database, 'intimacy/freaky/choke/syncChallenge'), {
      active: true,
      startTime: Date.now(),
      score: 0,
      taps: {}
    });
  };

  // Record sync tap
  const handleSyncTap = async () => {
    const tapTime = Date.now();
    const newTaps = [...syncTaps, tapTime];
    setSyncTaps(newTaps);
    await set(ref(database, `intimacy/freaky/choke/syncChallenge/taps/${userProfile?.name}`), newTaps);

    // Check if partner tapped within 500ms
    const partnerRecentTaps = partnerSyncTaps.filter(t => Math.abs(t - tapTime) < 500);
    if (partnerRecentTaps.length > 0) {
      const newScore = syncScore + 10;
      setSyncScore(newScore);
      await set(ref(database, 'intimacy/freaky/choke/syncChallenge/score'), newScore);
    }
  };

  // Stop sync challenge
  const handleStopSyncChallenge = async () => {
    await set(ref(database, 'intimacy/freaky/choke/syncChallenge'), {
      active: false,
      finalScore: syncScore
    });
    setSyncActive(false);
    setSyncTaps([]);
    setPartnerSyncTaps([]);
  };

  // Start edge timer (Henry sets secret duration)
  const handleStartEdgeTimer = async (duration, rewardMsg) => {
    await set(ref(database, 'intimacy/freaky/choke/edgeTimer'), {
      active: true,
      duration: duration,
      startTime: Date.now(),
      rewardMessage: rewardMsg,
      completed: false
    });
  };

  // Complete edge timer (triggered when time elapsed)
  const handleCompleteEdge = async () => {
    await set(ref(database, 'intimacy/freaky/choke/edgeTimer/completed'), true);
    setEdgeCompleted(true);
  };

  // Reset edge timer
  const handleResetEdge = async () => {
    await set(ref(database, 'intimacy/freaky/choke/edgeTimer'), null);
    setEdgeActive(false);
    setEdgeProgress(0);
    setEdgeCompleted(false);
  };

  // Record gasp (Omi only)
  const handleGasp = async () => {
    const gaspId = Date.now().toString();
    await set(ref(database, `intimacy/freaky/choke/gaspLog/${gaspId}`), {
      timestamp: new Date().toISOString(),
      user: userProfile?.name
    });
    setGaspCount(prev => prev + 1);
  };

  // Clear gasp log (Henry only)
  const handleClearGaspLog = async () => {
    await set(ref(database, 'intimacy/freaky/choke/gaspLog'), null);
    setGaspLog([]);
    setGaspCount(0);
  };

  const handleTaskResponse = async (response) => {
    if (!selectedTask) return;

    const taskRef = ref(database, `intimacy/freaky/tasks/${selectedTask.id}`);
    const meterRef = ref(database, 'intimacy/freaky/favorMeter');

    if (response === 'obey') {
      await set(taskRef, {
        ...selectedTask,
        status: 'accepted',
        acceptedBy: userProfile?.name,
        acceptedAt: new Date().toISOString(),
        deadline: new Date(Date.now() + selectedTask.timeframe * 60 * 60 * 1000).toISOString()
      });
    } else if (response === 'disobey') {
      // Immediately deduct penalty points for disobedience
      const penaltyAmount = selectedTask.penaltyPoints || 5;
      const newMeter = favorMeter - penaltyAmount;

      // Check if favor would go below 0 - activate punishment protocol
      if (newMeter < 0 && !activePunishment) {
        await activatePunishmentProtocol(penaltyAmount);
      } else {
        await set(meterRef, Math.max(0, newMeter));
      }

      await set(taskRef, {
        ...selectedTask,
        status: 'disobeyed',
        rejectedBy: userProfile?.name,
        rejectedAt: new Date().toISOString()
      });
    }

    setShowTaskDetailModal(false);
    setSelectedTask(null);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) {
      setIsUploading(false);
      return;
    }

    setIsUploading(true);
    setUploadFiles(prev => [...prev, ...files]);

    // Create previews for images/videos
    // Images use base64 (small enough for Firebase Realtime DB)
    // Videos use object URLs for preview, actual file uploaded to Storage on submit
    const newPreviews = [];
    let processed = 0;

    files.forEach((file, index) => {
      if (file.type.startsWith('image/')) {
        // Images: use base64 for preview and storage
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews[index] = { type: 'image', url: reader.result, isBase64: true };
          processed++;
          if (processed === files.length) {
            setUploadPreviews(prev => [...prev, ...newPreviews.filter(Boolean)]);
            setIsUploading(false);
          }
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('video/')) {
        // Videos: use object URL for preview, will upload to Storage on submit
        const objectUrl = URL.createObjectURL(file);
        newPreviews[index] = { type: 'video', url: objectUrl, isBase64: false, file: file };
        processed++;
        if (processed === files.length) {
          setUploadPreviews(prev => [...prev, ...newPreviews.filter(Boolean)]);
          setIsUploading(false);
        }
      } else {
        processed++;
        if (processed === files.length) {
          setUploadPreviews(prev => [...prev, ...newPreviews.filter(Boolean)]);
          setIsUploading(false);
        }
      }
    });
  };

  const handleRemoveFile = (index) => {
    // Clean up object URL if it's a video
    const preview = uploadPreviews[index];
    if (preview && preview.type === 'video' && !preview.isBase64 && preview.url) {
      URL.revokeObjectURL(preview.url);
    }
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
    setUploadPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleReenterFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  };

  const handleSubmitTask = async () => {
    // Allow submission without files if uploadType is 'none'
    const requiresProof = selectedTask?.uploadType && selectedTask.uploadType !== 'none';
    const isTextType = selectedTask?.uploadType === 'text';
    if (!selectedTask || !isFullscreen) return;
    // Validate based on submission type
    if (isTextType && !textSubmission.trim()) return;
    if (requiresProof && !isTextType && !uploadFiles.length) return;

    const now = new Date();
    const deadline = new Date(selectedTask.deadline);
    const isLate = now > deadline;

    const taskRef = ref(database, `intimacy/freaky/tasks/${selectedTask.id}`);
    const meterRef = ref(database, 'intimacy/freaky/favorMeter');

    // Store preview data so Henry can view the uploads
    // For videos, upload to Firebase Storage first to get a proper URL
    let uploadData;
    if (isTextType) {
      // For text submissions, store the text content
      uploadData = [{
        type: 'text',
        content: textSubmission.trim(),
        name: 'Text Response'
      }];
    } else {
      // Process each preview - upload videos to Storage, keep images as base64
      uploadData = [];
      for (let index = 0; index < uploadPreviews.length; index++) {
        const preview = uploadPreviews[index];
        const fileName = uploadFiles[index]?.name || `file_${index}`;

        if (preview.type === 'video' && preview.file) {
          // Upload video to Firebase Storage
          try {
            const videoFileName = `intimacy/videos/${selectedTask.id}_${Date.now()}_${fileName}`;
            const videoRef = storageRef(storage, videoFileName);
            await uploadBytes(videoRef, preview.file);
            const downloadUrl = await getDownloadURL(videoRef);
            uploadData.push({
              type: 'video',
              url: downloadUrl,
              name: fileName
            });
          } catch (error) {
            console.error('Error uploading video:', error);
            // Fallback: store file name only if upload fails
            uploadData.push({
              type: 'video',
              url: null,
              name: fileName,
              uploadError: true
            });
          }
        } else {
          // Images and other files - use base64
          uploadData.push({
            type: preview.type,
            url: preview.url,
            name: fileName
          });
        }
      }
    }

    // If task is late, auto-deny it
    if (isLate) {
      // Deduct full reward points for late submission
      const penaltyAmount = selectedTask.points || 10;
      const newMeter = favorMeter - penaltyAmount;

      // Check if favor would go below 0 - activate punishment protocol
      if (newMeter < 0 && !activePunishment) {
        await activatePunishmentProtocol(penaltyAmount);
      } else {
        await set(meterRef, Math.max(0, newMeter));
      }

      await set(taskRef, {
        ...selectedTask,
        status: 'auto_denied_late',
        submittedBy: userProfile?.name,
        submittedAt: now.toISOString(),
        uploadFileNames: isTextType ? ['Text Response'] : uploadFiles.map(f => f.name),
        uploadFileTypes: isTextType ? ['text/plain'] : uploadFiles.map(f => f.type),
        uploadFileCount: isTextType ? 1 : uploadFiles.length,
        uploadData: uploadData,
        isLate: true,
        autoDeniedAt: now.toISOString()
      });
    } else {
      await set(taskRef, {
        ...selectedTask,
        status: 'pending_review',
        submittedBy: userProfile?.name,
        submittedAt: now.toISOString(),
        uploadFileNames: isTextType ? ['Text Response'] : uploadFiles.map(f => f.name),
        uploadFileTypes: isTextType ? ['text/plain'] : uploadFiles.map(f => f.type),
        uploadFileCount: isTextType ? 1 : uploadFiles.length,
        uploadData: uploadData,
        isLate: false
      });
    }

    // Save all media to Tribute Archive for Henry
    if (uploadData.length > 0) {
      for (const upload of uploadData) {
        if (upload.url || upload.content) {
          const tributeId = Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
          const tributeRef = ref(database, `intimacy/freaky/tributes/${tributeId}`);
          await set(tributeRef, {
            taskId: selectedTask.id,
            taskTitle: selectedTask.title,
            mediaType: upload.type,
            mediaUrl: upload.url || null,
            textContent: upload.content || null,
            fileName: upload.name,
            submittedBy: userProfile?.name || 'Omi',
            submittedAt: now.toISOString(),
            isLate: isLate
          });
        }
      }
    }

    // Clean up object URLs to prevent memory leaks
    uploadPreviews.forEach(preview => {
      if (preview.type === 'video' && !preview.isBase64 && preview.url) {
        URL.revokeObjectURL(preview.url);
      }
    });

    setUploadFiles([]);
    setUploadPreviews([]);
    setTextSubmission('');
    setShowTaskDetailModal(false);
    setSelectedTask(null);
    // Reset file input to clear any cached file selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleReviewTask = async (approved, addToWall = false) => {
    if (!selectedTask) return;

    const taskRef = ref(database, `intimacy/freaky/tasks/${selectedTask.id}`);
    const meterRef = ref(database, 'intimacy/freaky/favorMeter');

    if (approved) {
      // Calculate total points: base points + bonus if awarded
      let totalPoints = selectedTask.points || 0;
      if (awardBonus && selectedTask.bonusPoints) {
        totalPoints += selectedTask.bonusPoints;
      }

      await set(taskRef, {
        ...selectedTask,
        status: 'completed',
        reviewedBy: userProfile?.name,
        reviewedAt: new Date().toISOString(),
        approved: true,
        bonusAwarded: awardBonus && selectedTask.bonusPoints > 0
      });

      // Add reward points (only if not late)
      if (!selectedTask.isLate) {
        const newMeter = Math.min(100, favorMeter + totalPoints);
        await set(meterRef, newMeter);
      }

      // Add to compliance wall if requested
      if (addToWall && selectedTask.uploadData?.some(u => u.type === 'image')) {
        // Add each image to the compliance wall
        for (const upload of selectedTask.uploadData) {
          if (upload.type === 'image' && upload.url) {
            const complianceId = Date.now().toString() + '_' + Math.random().toString(36).substr(2, 9);
            const complianceRef = ref(database, `intimacy/freaky/compliance/${complianceId}`);
            await set(complianceRef, {
              taskId: selectedTask.id,
              taskTitle: selectedTask.title,
              uploadedAt: selectedTask.submittedAt,
              approved: true,
              approvedBy: userProfile?.name,
              imageUrl: upload.url
            });
          }
        }
      }
    } else {
      await set(taskRef, {
        ...selectedTask,
        status: 'rejected_review',
        reviewedBy: userProfile?.name,
        reviewedAt: new Date().toISOString(),
        approved: false
      });

      // Deduct penalty for failed task
      const penaltyAmount = selectedTask.penaltyPoints || 5;
      const newMeter = favorMeter - penaltyAmount;

      // Check if favor would go below 0 - activate punishment protocol
      if (newMeter < 0 && !activePunishment) {
        await activatePunishmentProtocol(penaltyAmount);
      } else {
        await set(meterRef, Math.max(0, newMeter));
      }
    }

    setShowTaskDetailModal(false);
    setSelectedTask(null);
    setAwardBonus(false);
    setExpandedImage(null);
  };

  const handleBackToHome = () => {
    navigate('/home');
  };

  const handleArchiveTask = async (taskId) => {
    const taskToArchive = tasks.find(t => t.id === taskId);
    if (!taskToArchive) return;

    // Move task to archive
    const archiveRef = ref(database, `intimacy/freaky/archivedTasks/${taskId}`);
    await set(archiveRef, {
      ...taskToArchive,
      archivedAt: new Date().toISOString(),
      archivedBy: userProfile?.name
    });

    // Remove from active tasks
    const taskRef = ref(database, `intimacy/freaky/tasks/${taskId}`);
    await set(taskRef, null);
  };

  // Add content to vault (Henry only)
  const handleAddVaultContent = async () => {
    if (!newVaultContent.title.trim()) {
      alert('Please enter a title for the reward');
      return;
    }
    if (newVaultContent.type === 'text' && !newVaultContent.content.trim()) {
      alert('Please enter some text content');
      return;
    }
    if (newVaultContent.type !== 'text' && !newVaultContent.file) {
      console.error('No file found in state:', newVaultContent);
      alert('No file selected. Please try uploading the video again.');
      return;
    }

    setIsUploading(true);
    const contentId = Date.now().toString();
    let contentToSave = {
      type: newVaultContent.type,
      title: newVaultContent.title,
      mediaType: newVaultContent.mediaType,
      createdAt: new Date().toISOString(),
      createdBy: userProfile?.name
    };

    try {
      // For media files, upload to Firebase Storage
      if (newVaultContent.type !== 'text' && newVaultContent.file) {
        console.log('Uploading file to Firebase Storage:', newVaultContent.file.name);
        const fileName = `intimacy/vault/${contentId}_${newVaultContent.file.name}`;
        const fileRef = storageRef(storage, fileName);
        await uploadBytes(fileRef, newVaultContent.file);
        const downloadUrl = await getDownloadURL(fileRef);
        contentToSave.content = downloadUrl;
        contentToSave.storagePath = fileName; // Save path for potential deletion
        console.log('File uploaded successfully:', downloadUrl);
      } else {
        // For text content, save directly
        contentToSave.content = newVaultContent.content;
      }

      // Save to database
      const contentRef = ref(database, `intimacy/freaky/vaultContents/${contentId}`);
      await set(contentRef, contentToSave);
      console.log('Reward added to vault successfully');

      // Revoke the temporary preview URL
      if (newVaultContent.file) {
        URL.revokeObjectURL(newVaultContent.content);
      }

      setNewVaultContent({ type: 'text', title: '', content: '', mediaType: '', file: null });
      setShowAddVaultContent(false);
      // Reset vault file input to clear cached selection
      if (vaultFileInputRef.current) {
        vaultFileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error adding content to vault:', error);
      alert(`Failed to add reward to vault: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file upload for vault content
  const handleVaultFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    let type = 'image';
    if (file.type.startsWith('video/')) type = 'video';
    else if (file.type.startsWith('audio/')) type = 'audio';

    // Store file object for later upload to Firebase Storage
    // Create a preview URL for display
    const previewUrl = URL.createObjectURL(file);

    setNewVaultContent(prev => ({
      ...prev,
      type,
      content: previewUrl, // temporary preview URL
      mediaType: file.type,
      file: file // actual file for upload
    }));
  };

  // Delete vault content (Henry only)
  const handleDeleteVaultContent = async (contentId, storagePath) => {
    // Delete from Firebase Storage if it has a storage path
    if (storagePath) {
      try {
        const fileRef = storageRef(storage, storagePath);
        const { deleteObject } = await import('firebase/storage');
        await deleteObject(fileRef);
      } catch (error) {
        console.error('Error deleting vault file from storage:', error);
      }
    }

    const contentRef = ref(database, `intimacy/freaky/vaultContents/${contentId}`);
    await set(contentRef, null);
  };

  // Unlock vault and transfer contents to reward storage (when Omi reaches 100)
  const handleUnlockVault = async () => {
    if (favorMeter < 100 || vaultContents.length === 0) return;

    // Move all vault contents to reward storage
    for (const item of vaultContents) {
      const storageId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      const storageRef = ref(database, `intimacy/freaky/rewardStorage/${storageId}`);
      await set(storageRef, {
        ...item,
        unlockedAt: new Date().toISOString()
      });

      // Remove from vault
      const vaultItemRef = ref(database, `intimacy/freaky/vaultContents/${item.id}`);
      await set(vaultItemRef, null);
    }

    // Reset favor meter to 0
    const favorRef = ref(database, 'intimacy/freaky/favorMeter');
    await set(favorRef, 0);
  };

  // Protocol editing functions (Henry only)
  const handleOpenProtocolEditor = () => {
    setEditingProtocols([...protocols]);
    setShowProtocolEditor(true);
  };

  const handleProtocolTextChange = (index, newText) => {
    const updated = [...editingProtocols];
    updated[index] = { ...updated[index], text: newText };
    setEditingProtocols(updated);
  };

  const handleAddProtocol = () => {
    const newId = Date.now().toString();
    setEditingProtocols([...editingProtocols, { id: newId, text: '' }]);
  };

  const handleRemoveProtocol = (index) => {
    const updated = editingProtocols.filter((_, i) => i !== index);
    setEditingProtocols(updated);
  };

  const handleSaveProtocols = async () => {
    // Filter out empty protocols
    const validProtocols = editingProtocols.filter(p => p.text.trim());
    if (validProtocols.length === 0) return;

    // Save to Firebase
    const protocolsRef = ref(database, 'intimacy/freaky/protocols');
    const protocolsObj = {};
    validProtocols.forEach((protocol, index) => {
      protocolsObj[protocol.id || `protocol_${index}`] = { text: protocol.text };
    });
    await set(protocolsRef, protocolsObj);

    setShowProtocolEditor(false);
  };

  // Eros Poetry handlers
  const handleAddPoem = async () => {
    if (!newPoem.title.trim() || !newPoem.content.trim()) return;

    const poemId = Date.now().toString();
    const poemRef = ref(database, `intimacy/eros/poems/${poemId}`);
    await set(poemRef, {
      title: newPoem.title,
      content: newPoem.content,
      author: userProfile?.name || 'Anonymous',
      createdAt: new Date().toISOString()
    });

    setNewPoem({ title: '', content: '', author: '' });
    setShowAddPoem(false);
  };

  const handleDeletePoem = async (poemId) => {
    const poemRef = ref(database, `intimacy/eros/poems/${poemId}`);
    await set(poemRef, null);
    if (selectedPoem?.id === poemId) {
      setSelectedPoem(null);
    }
  };

  // Download all tributes as individual files
  const handleDownloadAllTributes = async () => {
    if (tributes.length === 0) return;

    for (const tribute of tributes) {
      // Skip if no media URL or text content
      if (!tribute.mediaUrl && !tribute.textContent) continue;

      const link = document.createElement('a');

      if (tribute.mediaType === 'text' && tribute.textContent) {
        // For text, create a text file
        const blob = new Blob([tribute.textContent], { type: 'text/plain' });
        link.href = URL.createObjectURL(blob);
        link.download = `${tribute.taskTitle || 'tribute'}_${tribute.id}.txt`;
      } else if (tribute.mediaUrl) {
        link.href = tribute.mediaUrl;
        const extension = tribute.mediaType === 'image' ? 'jpg' :
                         tribute.mediaType === 'video' ? 'mp4' :
                         tribute.mediaType === 'audio' ? 'webm' : 'file';
        link.download = tribute.fileName || `${tribute.taskTitle || 'tribute'}_${tribute.id}.${extension}`;
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Small delay between downloads to prevent browser blocking
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  // Whisper Vault handlers
  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = () => {
          setAudioChunks([reader.result]);
        };
        reader.readAsDataURL(blob);
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Error accessing microphone:', err);
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      // Stop microphone access
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      }
    }
  };

  const handleSaveWhisper = async () => {
    if (!newWhisperTitle.trim() || audioChunks.length === 0) return;

    const whisperId = Date.now().toString();
    const whisperRef = ref(database, `intimacy/eros/whispers/${whisperId}`);
    await set(whisperRef, {
      title: newWhisperTitle,
      audioData: audioChunks[0],
      author: userProfile?.name || 'Anonymous',
      createdAt: new Date().toISOString()
    });

    setNewWhisperTitle('');
    setAudioChunks([]);
  };

  const handleDeleteErosWhisper = async (whisperId) => {
    const whisperRef = ref(database, `intimacy/eros/whispers/${whisperId}`);
    await set(whisperRef, null);
    if (playingWhisper === whisperId) {
      setPlayingWhisper(null);
    }
  };

  const handlePlayWhisper = (whisperId) => {
    setPlayingWhisper(playingWhisper === whisperId ? null : whisperId);
  };

  // Filter tasks: active tasks vs archived-eligible (completed/rejected/disobeyed)
  const activeTasks = tasks.filter(t =>
    t.status === 'available' ||
    t.status === 'accepted' ||
    t.status === 'pending_review'
  );
  const completedTasks = tasks.filter(t =>
    t.status === 'completed' ||
    t.status === 'rejected_review' ||
    t.status === 'disobeyed' ||
    t.status === 'auto_denied_late' ||
    t.status === 'expired'
  );

  // Authentication Screen
  if (!isAuthenticated) {
    return (
      <div className="intimacy-page intimacy-auth">
        <div className="intimacy-background" />

        <motion.button
          className="intimacy-back-button"
          onClick={handleBackToHome}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ← Back
        </motion.button>

        <div className="intimacy-auth-container">
          <motion.div
            className="intimacy-auth-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.form
              onSubmit={handlePasswordSubmit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder=" "
                className="intimacy-auth-input"
                autoFocus
              />

              {error && (
                <motion.div
                  className="intimacy-error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {error}
                </motion.div>
              )}
            </motion.form>
          </motion.div>
        </div>
      </div>
    );
  }

  // Mode Selection Screen
  if (!selectedMode) {
    return (
      <div className="intimacy-page intimacy-mode-selection">
        <motion.button
          className="intimacy-back-button"
          onClick={handleBackToHome}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ← Back
        </motion.button>

        <motion.div
          className="mode-selection-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <motion.h1
            className="mode-selection-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Choose Your Mood
          </motion.h1>

          {partnerMode && (
            <motion.p
              className="partner-mode-indicator"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              {userProfile?.name === 'Omi' ? 'Henry' : 'Omi'} is in {partnerMode === 'eros' ? 'Eros' : 'Freaky'} mode
            </motion.p>
          )}

          <div className="mode-options">
            <motion.div
              className="mode-card eros-card"
              onClick={() => handleModeSelect('eros')}
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              whileHover={{ scale: 1.05, y: -10 }}
              whileTap={{ scale: 0.95 }}
            >
              <h2>Eros</h2>
              <p>Romantic & Sensual</p>
            </motion.div>

            <motion.div
              className="mode-card freaky-card"
              onClick={() => handleModeSelect('freaky')}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              whileHover={{ scale: 1.05, y: -10 }}
              whileTap={{ scale: 0.95 }}
            >
              <h2>Freaky</h2>
              <p>Roleplay & Control</p>
            </motion.div>
          </div>

        </motion.div>
      </div>
    );
  }

  // Protocol List Screen (Freaky Mode - Omi only)
  if (selectedMode === 'freaky' && !protocolAccepted && userProfile?.name === 'Omi') {
    return (
      <div className="intimacy-page intimacy-protocol">
        <div className="intimacy-background" />

        <motion.button
          className="intimacy-back-button"
          onClick={() => setSelectedMode(null)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ← Change Mode
        </motion.button>

        <div className="protocol-container">
          <motion.div
            className="protocol-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="protocol-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              Protocol Acknowledgment
            </motion.h1>

            <motion.p
              className="protocol-subtitle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              You must acknowledge each protocol to proceed
            </motion.p>

            <motion.div
              className="protocol-list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              {protocols.map((protocol, index) => (
                <motion.div
                  key={protocol.id}
                  className={`protocol-item ${acceptedProtocols.includes(protocol.id) ? 'accepted' : ''}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                >
                  <div className="protocol-text">{protocol.text}</div>
                  <motion.button
                    className="protocol-accept-btn"
                    onClick={() => handleProtocolAccept(protocol.id)}
                    disabled={acceptedProtocols.includes(protocol.id)}
                    whileHover={{ scale: acceptedProtocols.includes(protocol.id) ? 1 : 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {acceptedProtocols.includes(protocol.id) ? '✓ Accepted' : 'Accept'}
                  </motion.button>
                </motion.div>
              ))}
            </motion.div>

            <motion.button
              className="protocol-submit-btn"
              onClick={handleProtocolSubmit}
              disabled={acceptedProtocols.length !== protocols.length}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2 }}
              whileHover={{ scale: acceptedProtocols.length === protocols.length ? 1.02 : 1 }}
              whileTap={{ scale: 0.98 }}
            >
              {acceptedProtocols.length === protocols.length ? 'Proceed to Dashboard' : `Accept All Protocols (${acceptedProtocols.length}/${protocols.length})`}
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Freaky Sub-Mode Selection Screen
  if (selectedMode === 'freaky' && !selectedFreakyMode) {
    return (
      <div className="intimacy-page intimacy-freaky-selection">
        <div className="intimacy-background" />

        <motion.button
          className="intimacy-back-button"
          onClick={() => {
            setSelectedMode(null);
            setProtocolAccepted(false);
            setAcceptedProtocols([]);
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          ← Change Mode
        </motion.button>

        <div className="freaky-selection-container">
          <motion.h1
            className="freaky-selection-title"
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Options
          </motion.h1>

          <motion.p
            className="freaky-selection-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Explore our kinks
          </motion.p>

          <div className="freaky-mode-options">
            <motion.div
              className="freaky-mode-card command-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => setSelectedFreakyMode('command')}
            >
              <div className="command-symbol">
                <div className="void-ring"></div>
                <div className="void-ring"></div>
                <div className="void-ring"></div>
                <div className="void-core"></div>
              </div>
              <span className="scary-label">Command</span>
            </motion.div>

            <motion.div
              className="freaky-mode-card choke-card active"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => setSelectedFreakyMode('breathControl')}
            >
              <div className="choke-symbol">
                <div className="choke-ring"></div>
                <div className="choke-ring"></div>
                <div className="choke-ring"></div>
              </div>
              <span className="scary-label">Choke</span>
            </motion.div>

            <motion.div
              className="freaky-mode-card carnival-card"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.03, y: -5 }}
            >
              <div className="carnival-fireworks">
                {[...Array(12)].map((_, i) => (
                  <span key={i} className="firework-particle" style={{ '--i': i }} />
                ))}
              </div>
              <div className="freaky-mode-icon">🎪</div>
              <h2>Carnival</h2>
              <p>Maddness and Games</p>
              <div className="coming-soon-badge">Coming Soon</div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  // Main Intimacy Interface (after mode selected and protocols accepted)
  return (
    <div className={`intimacy-page intimacy-main ${selectedMode}-mode`}>
      <motion.button
        className="intimacy-back-button"
        onClick={() => {
          if (selectedMode === 'freaky' && selectedFreakyMode) {
            setSelectedFreakyMode(null);
          } else {
            setSelectedMode(null);
          }
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {selectedMode === 'freaky' && selectedFreakyMode ? '← Back to Selection' : '← Change Mode'}
      </motion.button>

      <motion.div
        className="intimacy-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {selectedMode === 'eros' && (
          <>
            <h1 className="intimacy-mode-title">Eros Mode</h1>
            <div className="intimacy-features">
              <motion.div
                className="feature-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                whileHover={{ scale: 1.03 }}
              >
                <h3>Heat Map</h3>
                <p>Show where to touch</p>
                <div className="coming-soon">Coming Soon</div>
              </motion.div>

              <motion.div
                className="feature-card active-feature"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => navigate('/eros-poetry')}
              >
                <h3>Eros Poetry</h3>
                <p>Romantic and lustful writings</p>
                {poems.length > 0 && <div className="feature-count">{poems.length} poems</div>}
              </motion.div>

              <motion.div
                className="feature-card active-feature"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => navigate('/fogged-mirror')}
              >
                <h3>Fogged Mirror</h3>
                <p>Wipe away the fog to reveal the figure</p>
              </motion.div>

              <motion.div
                className="feature-card active-feature"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                whileHover={{ scale: 1.03 }}
                onClick={() => setShowWhisperVault(true)}
              >
                <h3>Whisper Vault</h3>
                <p>Record and hear intimate voice messages</p>
                {whispers.length > 0 && <div className="feature-count">{whispers.length} whispers</div>}
              </motion.div>
            </div>

            {/* Poetry Modal */}
            <AnimatePresence>
              {showPoetry && (
                <motion.div
                  className="eros-modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => {
                    setShowPoetry(false);
                    setSelectedPoem(null);
                    setShowAddPoem(false);
                  }}
                >
                  <motion.div
                    className="eros-modal poetry-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="eros-modal-header">
                      <h2>Eros Poetry</h2>
                      <button
                        className="close-modal-btn"
                        onClick={() => {
                          setShowPoetry(false);
                          setSelectedPoem(null);
                          setShowAddPoem(false);
                        }}
                      >
                        x
                      </button>
                    </div>

                    {!selectedPoem && !showAddPoem && (
                      <>
                        <button
                          className="add-poem-btn"
                          onClick={() => setShowAddPoem(true)}
                        >
                          + Write New Poem
                        </button>

                        <div className="poems-list">
                          {poems.length === 0 ? (
                            <p className="no-poems">No poems yet. Write the first one...</p>
                          ) : (
                            poems.map((poem) => (
                              <motion.div
                                key={poem.id}
                                className="poem-card"
                                whileHover={{ scale: 1.02 }}
                                onClick={() => setSelectedPoem(poem)}
                              >
                                <h3>{poem.title}</h3>
                                <p className="poem-preview">
                                  {poem.content.substring(0, 80)}...
                                </p>
                                <div className="poem-meta">
                                  <span>{poem.author}</span>
                                  <span>{new Date(poem.createdAt).toLocaleDateString()}</span>
                                </div>
                              </motion.div>
                            ))
                          )}
                        </div>
                      </>
                    )}

                    {selectedPoem && (
                      <div className="poem-view">
                        <button
                          className="back-btn"
                          onClick={() => setSelectedPoem(null)}
                        >
                          Back
                        </button>
                        <h3 className="poem-title">{selectedPoem.title}</h3>
                        <div className="poem-content">
                          {selectedPoem.content.split('\n').map((line, i) => (
                            <p key={i}>{line || <br />}</p>
                          ))}
                        </div>
                        <div className="poem-footer">
                          <span className="poem-author">- {selectedPoem.author}</span>
                          <button
                            className="delete-poem-btn"
                            onClick={() => {
                              handleDeletePoem(selectedPoem.id);
                              setSelectedPoem(null);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    )}

                    {showAddPoem && (
                      <div className="add-poem-form">
                        <button
                          className="back-btn"
                          onClick={() => {
                            setShowAddPoem(false);
                            setNewPoem({ title: '', content: '', author: '' });
                          }}
                        >
                          Back
                        </button>
                        <div className="form-group">
                          <label>Title</label>
                          <input
                            type="text"
                            value={newPoem.title}
                            onChange={(e) => setNewPoem({ ...newPoem, title: e.target.value })}
                            placeholder="Give your poem a title..."
                          />
                        </div>
                        <div className="form-group">
                          <label>Your Poem</label>
                          <textarea
                            value={newPoem.content}
                            onChange={(e) => setNewPoem({ ...newPoem, content: e.target.value })}
                            placeholder="Write your heart out..."
                            rows={10}
                          />
                        </div>
                        <button
                          className="save-poem-btn"
                          onClick={handleAddPoem}
                          disabled={!newPoem.title.trim() || !newPoem.content.trim()}
                        >
                          Save Poem
                        </button>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Whisper Vault Modal */}
            <AnimatePresence>
              {showWhisperVault && (
                <motion.div
                  className="eros-modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => {
                    setShowWhisperVault(false);
                    setIsRecording(false);
                    setAudioChunks([]);
                    setNewWhisperTitle('');
                  }}
                >
                  <motion.div
                    className="eros-modal whisper-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="eros-modal-header">
                      <h2>Whisper Vault</h2>
                      <button
                        className="close-modal-btn"
                        onClick={() => {
                          setShowWhisperVault(false);
                          setIsRecording(false);
                          setAudioChunks([]);
                          setNewWhisperTitle('');
                        }}
                      >
                        x
                      </button>
                    </div>

                    {/* Recording Section */}
                    <div className="whisper-record-section">
                      <h3>Record a Whisper</h3>
                      <div className="record-controls">
                        {!isRecording && audioChunks.length === 0 && (
                          <motion.button
                            className="record-btn"
                            onClick={handleStartRecording}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Start Recording
                          </motion.button>
                        )}
                        {isRecording && (
                          <motion.button
                            className="record-btn recording"
                            onClick={handleStopRecording}
                            animate={{ scale: [1, 1.1, 1] }}
                            transition={{ repeat: Infinity, duration: 1.5 }}
                          >
                            Stop Recording
                          </motion.button>
                        )}
                        {!isRecording && audioChunks.length > 0 && (
                          <div className="whisper-preview">
                            <audio src={audioChunks[0]} controls />
                            <input
                              type="text"
                              value={newWhisperTitle}
                              onChange={(e) => setNewWhisperTitle(e.target.value)}
                              placeholder="Name this whisper..."
                            />
                            <div className="whisper-actions">
                              <button
                                className="discard-btn"
                                onClick={() => {
                                  setAudioChunks([]);
                                  setNewWhisperTitle('');
                                }}
                              >
                                Discard
                              </button>
                              <button
                                className="save-whisper-btn"
                                onClick={handleSaveWhisper}
                                disabled={!newWhisperTitle.trim()}
                              >
                                Save Whisper
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Whispers List */}
                    <div className="whispers-list">
                      <h3>Your Whispers</h3>
                      {whispers.length === 0 ? (
                        <p className="no-whispers">No whispers yet. Record the first one...</p>
                      ) : (
                        whispers.map((whisper) => (
                          <motion.div
                            key={whisper.id}
                            className="whisper-card"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                          >
                            <div className="whisper-info">
                              <h4>{whisper.title}</h4>
                              <span className="whisper-meta">
                                {whisper.author} - {new Date(whisper.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <div className="whisper-controls">
                              <audio src={whisper.audioData} controls />
                              <button
                                className="delete-whisper-btn"
                                onClick={() => handleDeleteErosWhisper(whisper.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </motion.div>
                        ))
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {selectedMode === 'freaky' && selectedFreakyMode === 'command' && (
          <>
            <div className="freaky-header">
              {/* Circular Meter with integrated title and vault */}
              <motion.div
                className="command-meter-container"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.6 }}
              >
                <div className="meter-circular">
                  <svg className="meter-ring" viewBox="0 0 120 120">
                    {/* Background ring */}
                    <circle
                      className="meter-ring-bg"
                      cx="60"
                      cy="60"
                      r="52"
                      fill="none"
                      strokeWidth="8"
                    />
                    {/* Progress ring */}
                    <circle
                      className="meter-ring-progress"
                      cx="60"
                      cy="60"
                      r="52"
                      fill="none"
                      strokeWidth="8"
                      strokeDasharray={`${favorMeter * 3.267} 326.7`}
                      strokeLinecap="round"
                      transform="rotate(-90 60 60)"
                    />
                  </svg>
                  <div className="meter-center-content">
                    <span className="meter-value">{favorMeter}</span>
                    <span className="meter-label">favor</span>
                  </div>
                </div>

                <h1 className="command-title">Commands</h1>

                <div
                  className={`vault-button ${favorMeter >= 100 ? 'unlockable' : ''}`}
                  onClick={() => setShowRewardVault(true)}
                  title={favorMeter >= 100 ? 'Vault Ready to Unlock!' : `${100 - favorMeter} more points to unlock`}
                >
                  <svg className="vault-svg-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="6" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="13" r="2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 15V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M7 6V5C7 3.34315 8.34315 2 10 2H14C15.6569 2 17 3.34315 17 5V6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                  {vaultContents.length > 0 && (
                    <span className="vault-count">{vaultContents.length}</span>
                  )}
                </div>

                {/* Punishment Status Indicator */}
                {activePunishment && (
                  <motion.div
                    className="punishment-status-indicator"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => {
                      if (userProfile?.name === 'Henry') {
                        setShowPunishmentManager(true);
                      } else {
                        setShowPunishmentPopup(true);
                      }
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>Punishment Active</span>
                  </motion.div>
                )}
              </motion.div>
            </div>

            {/* Compliance Wall Background */}
            <div className="compliance-wall-background">
              <div className="compliance-grid-background">
                {complianceItems.map((item) => (
                  <motion.div
                    key={item.id}
                    className="compliance-item-bg"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                  >
                    <img src={item.imageUrl} alt="Compliance" />
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="freaky-dashboard">
              {/* Floating Tasks Container */}
              <div className="floating-tasks-container">
                {activeTasks.length === 0 && completedTasks.length === 0 ? (
                  <motion.div
                    className="no-tasks-floating"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <p>No tasks available</p>
                  </motion.div>
                ) : (
                  <div className="tasks-grid">
                    {/* Active Tasks */}
                    {activeTasks.map((task, index) => {
                      const isActive = task.status === 'accepted';
                      const isPending = task.status === 'pending_review';

                      return (
                        <motion.div
                          key={task.id}
                          className={`task-grid-card ${isActive ? 'active-task' : ''} ${isPending ? 'pending-task' : ''}`}
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={{
                            opacity: 1,
                            scale: 1,
                            y: 0,
                          }}
                          transition={{
                            opacity: { duration: 0.4, delay: index * 0.08 },
                            scale: { duration: 0.4, delay: index * 0.08, type: "spring", stiffness: 200 },
                            y: { duration: 0.4, delay: index * 0.08 }
                          }}
                          whileHover={{ scale: 1.03, y: -3 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleTaskClick(task)}
                        >
                          <div className="task-grid-points">
                            {task.points} pts
                            {task.bonusPoints > 0 && <span className="bonus-indicator">+{task.bonusPoints}</span>}
                          </div>
                          <h3>{task.title}</h3>
                          <p className="task-grid-status">
                            {isActive ? 'Active' : isPending ? 'Pending Review' : 'Available'}
                          </p>
                        </motion.div>
                      );
                    })}

                    {/* Completed Tasks - Dimmed, can be dismissed */}
                    {completedTasks.map((task, index) => {
                      const statusClass = task.status === 'completed' ? 'approved' :
                                         task.status === 'rejected_review' ? 'rejected' :
                                         task.status === 'disobeyed' ? 'disobeyed' :
                                         task.status === 'expired' ? 'late' : 'late';

                      return (
                        <motion.div
                          key={task.id}
                          className={`task-grid-card completed-task ${statusClass}`}
                          initial={{ opacity: 0, scale: 0.8, y: 20 }}
                          animate={{
                            opacity: 0.6,
                            scale: 1,
                            y: 0,
                          }}
                          transition={{
                            opacity: { duration: 0.4, delay: index * 0.08 },
                            scale: { duration: 0.4, delay: index * 0.08 },
                            y: { duration: 0.4, delay: index * 0.08 }
                          }}
                          whileHover={{ opacity: 1, y: -2 }}
                        >
                          <div className="task-grid-points">
                            {task.points} pts
                            {task.bonusPoints > 0 && <span className="bonus-indicator">+{task.bonusPoints}</span>}
                          </div>
                          <h3>{task.title}</h3>
                          <p className="task-grid-status">
                            {task.status === 'completed' ? 'Approved' :
                             task.status === 'rejected_review' ? 'Rejected' :
                             task.status === 'disobeyed' ? 'Disobeyed' :
                             task.status === 'expired' ? 'Expired' : 'Late'}
                          </p>
                          <button
                            className="archive-task-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveTask(task.id);
                            }}
                          >
                            Dismiss
                          </button>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Control buttons */}
              <div className="dashboard-controls">
                {userProfile?.name === 'Henry' && (
                  <>
                    <button className="create-task-btn" onClick={() => setShowTaskModal(true)}>
                      + Create New Task
                    </button>
                    <button className="edit-protocols-btn" onClick={handleOpenProtocolEditor}>
                      Edit Protocols
                    </button>
                    <button
                      className={`punishment-manager-btn ${activePunishment ? 'has-active' : ''}`}
                      onClick={() => setShowPunishmentManager(true)}
                    >
                      Punishments {activePunishment && '(Active)'}
                    </button>
                    <button className="tribute-archive-btn" onClick={() => setShowTributeArchive(true)}>
                      Tribute Archive {tributes.length > 0 && `(${tributes.length})`}
                    </button>
                  </>
                )}
                {userProfile?.name === 'Omi' && favorMeter >= 0 && (
                  <button
                    className={`reward-storage-btn ${activePunishment ? 'locked' : ''}`}
                    onClick={() => {
                      if (activePunishment) {
                        // Show punishment popup again if trying to access locked rewards
                        setShowPunishmentPopup(true);
                      } else {
                        setShowRewardStorage(true);
                      }
                    }}
                  >
                    {activePunishment ? 'Rewards Locked' : rewardStorage.length > 0 ? `Henry's Gifts (${rewardStorage.length})` : "Henry's Gifts"}
                  </button>
                )}
                {completedTasks.length > 0 && (
                  <button
                    className="archive-all-btn"
                    onClick={() => completedTasks.forEach(t => handleArchiveTask(t.id))}
                  >
                    Dismiss All Completed ({completedTasks.length})
                  </button>
                )}
                <button
                  className="toggle-archive-btn"
                  onClick={() => setShowArchive(!showArchive)}
                >
                  {showArchive ? 'Hide Archive' : 'View Archive'}
                </button>
              </div>
            </div>

            {/* Task Creation Modal - Henry Only */}
            <AnimatePresence>
              {showTaskModal && userProfile?.name === 'Henry' && (
                <motion.div
                  className="task-modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowTaskModal(false)}
                >
                  <motion.div
                    className="task-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2>Create New Task</h2>

                    <div className="task-form">
                      <div className="form-group">
                        <label>Task Title</label>
                        <input
                          type="text"
                          value={newTask.title}
                          onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                          placeholder="Enter task title"
                        />
                      </div>

                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={newTask.description}
                          onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                          placeholder="Describe the task"
                          rows="4"
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Points (Reward)</label>
                          <input
                            type="number"
                            value={newTask.points}
                            onChange={(e) => setNewTask({ ...newTask, points: parseInt(e.target.value) })}
                            min="1"
                            max="100"
                          />
                        </div>

                        <div className="form-group">
                          <label>Penalty Points (Disobey)</label>
                          <input
                            type="number"
                            value={newTask.penaltyPoints}
                            onChange={(e) => setNewTask({ ...newTask, penaltyPoints: parseInt(e.target.value) })}
                            min="1"
                            max="50"
                          />
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Timeframe (hours)</label>
                          <input
                            type="number"
                            value={newTask.timeframe}
                            onChange={(e) => setNewTask({ ...newTask, timeframe: parseInt(e.target.value) })}
                            min="1"
                            max="168"
                          />
                        </div>

                        <div className="form-group">
                          <label>Upload Required</label>
                          <select
                            value={newTask.uploadType}
                            onChange={(e) => setNewTask({ ...newTask, uploadType: e.target.value })}
                          >
                            <option value="none">None</option>
                            <option value="photo">Photo</option>
                            <option value="video">Video</option>
                            <option value="audio">Audio</option>
                            <option value="text">Text Response</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Bonus Challenge (Optional)</label>
                        <textarea
                          value={newTask.bonusChallenge}
                          onChange={(e) => setNewTask({ ...newTask, bonusChallenge: e.target.value })}
                          placeholder="Add an optional bonus challenge for extra points"
                          rows="3"
                        />
                      </div>

                      {newTask.bonusChallenge && (
                        <div className="form-group">
                          <label>Bonus Points</label>
                          <input
                            type="number"
                            value={newTask.bonusPoints}
                            onChange={(e) => setNewTask({ ...newTask, bonusPoints: parseInt(e.target.value) })}
                            min="1"
                            max="50"
                          />
                        </div>
                      )}

                      <div className="modal-buttons">
                        <button className="cancel-btn" onClick={() => setShowTaskModal(false)}>
                          Cancel
                        </button>
                        <button
                          className="create-btn"
                          onClick={handleCreateTask}
                          disabled={!newTask.title || !newTask.description}
                        >
                          Create Task
                        </button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Task Detail Modal - For Viewing/Accepting */}
            <AnimatePresence>
              {showTaskDetailModal && selectedTask && (
                <motion.div
                  className={`task-modal-overlay ${(isUploading || uploadFiles.length > 0) ? 'no-close' : ''}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => {
                    // Prevent closing if Omi is uploading or has uploaded a file
                    if (isUploading || uploadFiles.length > 0) {
                      return;
                    }
                    // Only allow Henry to close by clicking outside
                    // Omi must make a choice (obey/disobey) when task is available
                    if (userProfile?.name === 'Henry' || (selectedTask.status !== 'available' && selectedTask.status !== 'accepted')) {
                      setShowTaskDetailModal(false);
                    }
                  }}
                >
                  <motion.div
                    className="task-modal task-detail-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2>{selectedTask.title}</h2>

                    <div className="task-detail-content">
                      <div className="task-detail-header">
                        <div className="task-detail-points">
                          <span className="points-label">Reward</span>
                          <span className="points-value">+{selectedTask.points}</span>
                        </div>

                        <div className="task-detail-penalty">
                          <span className="penalty-label">Penalty</span>
                          <span className="penalty-value">-{selectedTask.penaltyPoints || 5}</span>
                        </div>

                        <div className="task-detail-timeframe">
                          <span className="timeframe-label">Time Limit</span>
                          <span className="timeframe-value">{selectedTask.timeframe}h</span>
                        </div>

                        {selectedTask.uploadType && selectedTask.uploadType !== 'none' && (
                          <div className="task-detail-upload">
                            <div className="upload-label">Proof Required</div>
                            <div className="upload-type">{selectedTask.uploadType}</div>
                          </div>
                        )}
                      </div>

                      <div className="task-detail-description">
                        <h3>Instructions</h3>
                        <p>{selectedTask.description}</p>
                      </div>

                      {selectedTask.bonusChallenge && selectedTask.bonusChallenge.trim() !== '' && (
                        <div className="task-bonus-section">
                          <div className="bonus-header">
                            Bonus Challenge
                            <span className="bonus-points">+{selectedTask.bonusPoints || 0} pts</span>
                          </div>
                          <p className="bonus-description">{selectedTask.bonusChallenge}</p>
                        </div>
                      )}

                      {/* Omi - Available Tasks */}
                      {userProfile?.name === 'Omi' && selectedTask.status === 'available' && (
                        <div className="task-response-buttons">
                          <motion.button
                            className="obey-btn"
                            onClick={() => handleTaskResponse('obey')}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            Obey
                          </motion.button>
                          <motion.button
                            className="disobey-btn"
                            onClick={() => handleTaskResponse('disobey')}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            Disobey
                          </motion.button>
                        </div>
                      )}

                      {/* Omi - Active Tasks (Upload Section) */}
                      {userProfile?.name === 'Omi' && selectedTask.status === 'accepted' && (
                        <div className="task-upload-section">
                          <div className="deadline-warning">
                            <p>Deadline: {new Date(selectedTask.deadline).toLocaleString()}</p>
                          </div>

                          {/* Only show upload section if proof is required */}
                          {selectedTask.uploadType && selectedTask.uploadType !== 'none' ? (
                            <>
                              {selectedTask.uploadType === 'text' ? (
                                <div className="text-submission-area">
                                  <label className="text-submission-label">Your Response:</label>
                                  <textarea
                                    className="text-submission-input"
                                    value={textSubmission}
                                    onChange={(e) => setTextSubmission(e.target.value)}
                                    placeholder="Write your response here..."
                                    rows="6"
                                  />
                                  {textSubmission.length > 0 && (
                                    <p className="text-char-count">{textSubmission.length} characters</p>
                                  )}
                                </div>
                              ) : (
                                <div className="upload-area">
                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    id="task-upload"
                                    multiple={selectedTask.uploadType === 'photo'}
                                    accept={
                                      selectedTask.uploadType === 'photo' ? 'image/*' :
                                      selectedTask.uploadType === 'video' ? 'video/*' :
                                      selectedTask.uploadType === 'audio' ? 'audio/*' :
                                      '*/*'
                                    }
                                    onChange={handleFileUpload}
                                    style={{ display: 'none' }}
                                  />
                                  <label htmlFor="task-upload" className="upload-label-btn">
                                    {uploadFiles.length > 0
                                      ? `${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''} selected - Add more`
                                      : 'Choose Files'}
                                  </label>

                                  {uploadPreviews.length > 0 && (
                                    <div className="upload-previews-grid">
                                      {uploadPreviews.map((preview, index) => (
                                        <div key={index} className="upload-preview-item">
                                          {preview.type === 'image' && (
                                            <img src={preview.url} alt={`Preview ${index + 1}`} />
                                          )}
                                          {preview.type === 'video' && (
                                            <video src={preview.url} controls />
                                          )}
                                          <button
                                            className="remove-file-btn"
                                            onClick={() => handleRemoveFile(index)}
                                            type="button"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Re-enter fullscreen button - required after file upload */}
                              {!isFullscreen && uploadFiles.length > 0 && selectedTask.uploadType !== 'text' && (
                                <motion.button
                                  className="reenter-fullscreen-btn"
                                  onClick={handleReenterFullscreen}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  whileHover={{ scale: 1.02 }}
                                  whileTap={{ scale: 0.98 }}
                                >
                                  Re-enter Fullscreen to Submit
                                </motion.button>
                              )}
                            </>
                          ) : null}

                          <button
                            className="submit-task-btn"
                            onClick={handleSubmitTask}
                            disabled={(() => {
                              const requiresProof = selectedTask?.uploadType && selectedTask.uploadType !== 'none';
                              const isTextType = selectedTask?.uploadType === 'text';
                              // Must be in fullscreen to submit
                              if (!isFullscreen) return true;
                              // If text type, must have text content
                              if (isTextType && !textSubmission.trim()) return true;
                              // If file proof is required, must have files uploaded
                              if (requiresProof && !isTextType && !uploadFiles.length) return true;
                              return false;
                            })()}
                          >
                            {(() => {
                              const requiresProof = selectedTask?.uploadType && selectedTask.uploadType !== 'none';
                              const isTextType = selectedTask?.uploadType === 'text';
                              // If in fullscreen, show normal submit text
                              if (isFullscreen) {
                                if (isTextType && !textSubmission.trim()) {
                                  return 'Write Response to Submit';
                                }
                                if (requiresProof && !isTextType && !uploadFiles.length) {
                                  return 'Upload Proof to Submit';
                                }
                                return 'Submit for Review';
                              }
                              // Not in fullscreen - show appropriate message
                              if (uploadFiles.length > 0) {
                                return 'Must Re-enter Fullscreen';
                              }
                              return 'Enter Fullscreen to Submit';
                            })()}
                          </button>

                          <button
                            className="close-detail-btn omi-close"
                            onClick={() => {
                              setShowTaskDetailModal(false);
                              setUploadFiles([]);
                              setUploadPreviews([]);
                              setTextSubmission('');
                              // Reset file input to clear any cached file selection
                              if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                              }
                            }}
                          >
                            Close
                          </button>
                        </div>
                      )}

                      {/* Omi - Awaiting Review (Suspenseful) */}
                      {userProfile?.name === 'Omi' && selectedTask.status === 'pending_review' && (
                        <div className="awaiting-review-section">
                          <motion.div
                            className="awaiting-review-content"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8 }}
                          >
                            <motion.div
                              className="awaiting-icon"
                              animate={{
                                scale: [1, 1.1, 1],
                                opacity: [0.7, 1, 0.7]
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            >
                              ⏳
                            </motion.div>
                            <h3 className="awaiting-title">Awaiting Judgment</h3>
                            <p className="awaiting-text">
                              Your submission has been received...
                            </p>
                            <p className="awaiting-subtext">
                              Your fate is now in his hands.
                            </p>
                          </motion.div>
                        </div>
                      )}

                      {/* Omi - Auto Denied Late Submission */}
                      {userProfile?.name === 'Omi' && selectedTask.status === 'auto_denied_late' && (
                        <div className="auto-denied-section">
                          <motion.div
                            className="auto-denied-content"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8 }}
                          >
                            <div className="auto-denied-icon">⏰</div>
                            <h3 className="auto-denied-title">Too Late</h3>
                            <p className="auto-denied-text">
                              Your submission was past the deadline.
                            </p>
                            <p className="auto-denied-penalty">
                              -{selectedTask.points || 10} points deducted
                            </p>
                            <button
                              className="close-detail-btn"
                              onClick={() => setShowTaskDetailModal(false)}
                            >
                              Accept Fate
                            </button>
                          </motion.div>
                        </div>
                      )}

                      {/* Omi - Expired Task */}
                      {userProfile?.name === 'Omi' && selectedTask.status === 'expired' && (
                        <div className="auto-denied-section">
                          <motion.div
                            className="auto-denied-content"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.8 }}
                          >
                            <h3 className="auto-denied-title">Expired</h3>
                            <p className="auto-denied-text">
                              You failed to complete this task before the deadline.
                            </p>
                            <p className="auto-denied-penalty">
                              -{selectedTask.points || 10} points deducted
                            </p>
                            <button
                              className="close-detail-btn"
                              onClick={() => setShowTaskDetailModal(false)}
                            >
                              Accept Fate
                            </button>
                          </motion.div>
                        </div>
                      )}

                      {/* Henry - Review Tasks */}
                      {userProfile?.name === 'Henry' && selectedTask.status === 'pending_review' && (
                        <div className="task-review-section">
                          <div className="review-info">
                            <p>Submitted: {new Date(selectedTask.submittedAt).toLocaleString()}</p>
                            {selectedTask.isLate && (
                              <p className="late-warning">Submitted Late - No reward points</p>
                            )}
                          </div>

                          {/* Show uploaded content for review */}
                          {selectedTask.uploadData && selectedTask.uploadData.length > 0 ? (
                            <div className="review-uploads">
                              <h4>Submitted Evidence ({selectedTask.uploadData.length} {selectedTask.uploadType === 'text' ? 'response' : `file${selectedTask.uploadData.length > 1 ? 's' : ''}`})</h4>
                              {selectedTask.uploadType !== 'text' && (
                                <p className="click-to-expand-hint">Click to expand</p>
                              )}
                              <div className={`review-uploads-grid ${selectedTask.uploadType === 'text' ? 'text-response' : ''}`}>
                                {selectedTask.uploadData.map((upload, index) => (
                                  <div
                                    key={index}
                                    className={`review-upload-item ${upload.type === 'text' ? 'text-item' : ''}`}
                                    onClick={() => upload.type === 'image' && upload.url && setExpandedImage(upload.url)}
                                  >
                                    {upload.type === 'text' && upload.content && (
                                      <div className="text-response-content">
                                        <p className="text-response-text">{upload.content}</p>
                                      </div>
                                    )}
                                    {upload.type === 'image' && upload.url && (
                                      <img src={upload.url} alt={`Submission ${index + 1}`} />
                                    )}
                                    {upload.type === 'video' && upload.url && (
                                      <div
                                        className="video-preview-wrapper"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setExpandedVideo(upload.url);
                                        }}
                                      >
                                        <video src={upload.url} muted />
                                        <div className="video-expand-icon">▶</div>
                                      </div>
                                    )}
                                    {upload.type !== 'text' && !upload.url && (
                                      <div className="upload-unavailable">
                                        <p>File: {upload.name || `file_${index + 1}`}</p>
                                        <p className="unavailable-note">Preview unavailable</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div className="review-uploads">
                              <h4>Submitted Evidence</h4>
                              <p className="no-preview-text">
                                {selectedTask.uploadFileCount || 0} file(s) submitted
                                {selectedTask.uploadFileNames && selectedTask.uploadFileNames.length > 0 && (
                                  <span>: {selectedTask.uploadFileNames.join(', ')}</span>
                                )}
                              </p>
                            </div>
                          )}

                          {/* Bonus Points Checkbox */}
                          {selectedTask.bonusChallenge && selectedTask.bonusPoints > 0 && (
                            <div className="bonus-award-section">
                              <label className="bonus-checkbox-label">
                                <input
                                  type="checkbox"
                                  checked={awardBonus}
                                  onChange={(e) => setAwardBonus(e.target.checked)}
                                  className="bonus-checkbox"
                                />
                                <span className="bonus-checkbox-text">
                                  Award Bonus Points (+{selectedTask.bonusPoints})
                                </span>
                              </label>
                              <p className="bonus-challenge-reminder">
                                Bonus Challenge: {selectedTask.bonusChallenge}
                              </p>
                            </div>
                          )}

                          <div className="review-buttons">
                            <motion.button
                              className="reject-review-btn"
                              onClick={() => handleReviewTask(false)}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              Reject
                            </motion.button>
                            <motion.button
                              className="approve-review-btn"
                              onClick={() => handleReviewTask(true, false)}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              Approve
                            </motion.button>
                            {(selectedTask.uploadFileTypes?.some(t => t.startsWith('image/')) || selectedTask.uploadData?.some(u => u.type === 'image')) && (
                              <motion.button
                                className="approve-wall-btn"
                                onClick={() => handleReviewTask(true, true)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                Approve + Add to Wall
                              </motion.button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Status Display for Other States */}
                      {!((userProfile?.name === 'Omi' && selectedTask.status === 'available') ||
                          (userProfile?.name === 'Omi' && selectedTask.status === 'accepted') ||
                          (userProfile?.name === 'Omi' && selectedTask.status === 'pending_review') ||
                          (userProfile?.name === 'Omi' && selectedTask.status === 'auto_denied_late') ||
                          (userProfile?.name === 'Omi' && selectedTask.status === 'expired') ||
                          (userProfile?.name === 'Henry' && selectedTask.status === 'pending_review')) && (
                        <div className="task-status-display">
                          <p className={`status-text ${selectedTask.status}`}>
                            Status: {selectedTask.status === 'accepted' ? 'Active' :
                                    selectedTask.status === 'pending_review' ? 'Pending Review' :
                                    selectedTask.status === 'rejected_review' ? 'Rejected' :
                                    selectedTask.status === 'auto_denied_late' ? 'Auto-Denied (Late)' :
                                    selectedTask.status === 'expired' ? 'Expired (Deadline Passed)' :
                                    selectedTask.status}
                          </p>
                        </div>
                      )}

                      {userProfile?.name === 'Henry' && (
                        <button className="close-detail-btn" onClick={() => setShowTaskDetailModal(false)}>
                          Close
                        </button>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Expanded Image Modal */}
            <AnimatePresence>
              {expandedImage && (
                <motion.div
                  className="expanded-image-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setExpandedImage(null)}
                >
                  <motion.div
                    className="expanded-image-container"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img src={expandedImage} alt="Expanded submission" />
                    <button
                      className="close-expanded-btn"
                      onClick={() => setExpandedImage(null)}
                    >
                      Close
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Expanded Video Modal */}
            <AnimatePresence>
              {expandedVideo && (
                <motion.div
                  className="expanded-video-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setExpandedVideo(null)}
                >
                  <motion.div
                    className="expanded-video-container"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <video src={expandedVideo} controls autoPlay />
                    <button
                      className="close-expanded-btn"
                      onClick={() => setExpandedVideo(null)}
                    >
                      Close
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Expanded Audio Modal */}
            <AnimatePresence>
              {expandedAudio && (
                <motion.div
                  className="expanded-video-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setExpandedAudio(null)}
                >
                  <motion.div
                    className="expanded-video-container"
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <audio src={expandedAudio} controls autoPlay style={{ width: '100%' }} />
                    <button
                      className="close-expanded-btn"
                      onClick={() => setExpandedAudio(null)}
                    >
                      Close
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Archive Modal */}
            <AnimatePresence>
              {showArchive && (
                <motion.div
                  className="archive-modal-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowArchive(false)}
                >
                  <motion.div
                    className="archive-modal"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 50 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2>Task Archive</h2>
                    <div className="archive-list">
                      {archivedTasks.length === 0 ? (
                        <p className="no-archived-tasks">No archived tasks yet</p>
                      ) : (
                        archivedTasks.map((task) => (
                          <div
                            key={task.id}
                            className={`archive-task-item ${task.status}`}
                          >
                            <div className="archive-task-header">
                              <div className="archive-task-info">
                                <h4>{task.title}</h4>
                                <p className="archive-task-status">
                                  {task.status === 'completed' ? 'Approved' :
                                   task.status === 'rejected_review' ? 'Rejected' :
                                   task.status === 'disobeyed' ? 'Disobeyed' :
                                   task.status === 'auto_denied_late' ? 'Late' :
                                   task.status === 'expired' ? 'Expired' : task.status}
                                </p>
                                <p className="archive-task-date">
                                  Archived: {new Date(task.archivedAt).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="archive-task-points">
                                {task.status === 'completed' ? '+' : '-'}{task.points} pts
                              </div>
                            </div>
                            {task.description && (
                              <p className="archive-task-description">{task.description}</p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                    <button
                      className="close-archive-btn"
                      onClick={() => setShowArchive(false)}
                    >
                      Close
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Reward Vault Modal */}
            <AnimatePresence>
              {showRewardVault && (
                <motion.div
                  className="reward-vault-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => {
                    setShowRewardVault(false);
                    setShowAddVaultContent(false);
                  }}
                >
                  <motion.div
                    className="reward-vault-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Henry's View - Add content to vault */}
                    {userProfile?.name === 'Henry' && (
                      <>
                        <div className="reward-vault-header">
                          <h2>Reward Vault</h2>
                          <div className="vault-status-badge">
                            <span>{vaultContents.length} items inside</span>
                          </div>
                        </div>

                        <p className="vault-description">
                          Add rewards for Omi to unlock when she reaches 100 favor points.
                        </p>

                        {/* Add Content Button */}
                        {!showAddVaultContent && (
                          <button
                            className="add-reward-btn"
                            onClick={() => setShowAddVaultContent(true)}
                          >
                            + Add Reward to Vault
                          </button>
                        )}

                        {/* Add Content Form */}
                        {showAddVaultContent && (
                          <motion.div
                            className="create-reward-form"
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                          >
                            <div className="form-group">
                              <label>Title</label>
                              <input
                                type="text"
                                value={newVaultContent.title}
                                onChange={(e) => setNewVaultContent(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Give this reward a title..."
                              />
                            </div>

                            <div className="form-group">
                              <label>Content Type</label>
                              <div className="content-type-selector">
                                <button
                                  className={`type-btn ${newVaultContent.type === 'text' ? 'active' : ''}`}
                                  onClick={() => setNewVaultContent(prev => ({ ...prev, type: 'text', content: '', mediaType: '', file: null }))}
                                >
                                  Text
                                </button>
                                <label className={`type-btn ${['image', 'video', 'audio'].includes(newVaultContent.type) ? 'active' : ''}`}>
                                  Media
                                  <input
                                    ref={vaultFileInputRef}
                                    type="file"
                                    accept="image/*,video/*,audio/*"
                                    onChange={handleVaultFileUpload}
                                    style={{ display: 'none' }}
                                  />
                                </label>
                              </div>
                            </div>

                            {newVaultContent.type === 'text' && (
                              <div className="form-group">
                                <label>Message</label>
                                <textarea
                                  value={newVaultContent.content}
                                  onChange={(e) => setNewVaultContent(prev => ({ ...prev, content: e.target.value }))}
                                  placeholder="Write something special..."
                                  rows={4}
                                />
                              </div>
                            )}

                            {newVaultContent.type !== 'text' && newVaultContent.content && (
                              <div className="vault-media-preview">
                                {newVaultContent.type === 'image' && (
                                  <img src={newVaultContent.content} alt="Preview" />
                                )}
                                {newVaultContent.type === 'video' && (
                                  <video src={newVaultContent.content} controls />
                                )}
                                {newVaultContent.type === 'audio' && (
                                  <audio src={newVaultContent.content} controls />
                                )}
                              </div>
                            )}

                            {/* Re-enter fullscreen button for media uploads */}
                            {newVaultContent.type !== 'text' && newVaultContent.content && !isFullscreen && (
                              <motion.button
                                className="reenter-fullscreen-btn"
                                onClick={handleReenterFullscreen}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                Re-enter Fullscreen to Save
                              </motion.button>
                            )}

                            <div className="reward-form-buttons">
                              <button
                                className="cancel-reward-btn"
                                onClick={() => {
                                  setShowAddVaultContent(false);
                                  setNewVaultContent({ type: 'text', title: '', content: '', mediaType: '', file: null });
                                  // Reset vault file input to clear cached selection
                                  if (vaultFileInputRef.current) {
                                    vaultFileInputRef.current.value = '';
                                  }
                                }}
                                disabled={isUploading}
                              >
                                Cancel
                              </button>
                              <button
                                className="save-reward-btn"
                                onClick={handleAddVaultContent}
                                disabled={(() => {
                                  if (isUploading) return true;
                                  if (!newVaultContent.title.trim() || !newVaultContent.content) return true;
                                  // For media uploads, require fullscreen to save
                                  if (newVaultContent.type !== 'text' && !isFullscreen) return true;
                                  return false;
                                })()}
                              >
                                {(() => {
                                  if (isUploading) return 'Uploading...';
                                  if (newVaultContent.type !== 'text' && newVaultContent.content && !isFullscreen) {
                                    return 'Re-enter Fullscreen to Save';
                                  }
                                  return 'Add to Vault';
                                })()}
                              </button>
                            </div>
                          </motion.div>
                        )}

                        {/* Vault Contents List */}
                        <div className="vault-contents-list">
                          <h3>Vault Contents</h3>
                          {vaultContents.length === 0 ? (
                            <p className="no-rewards">Vault is empty - add some rewards!</p>
                          ) : (
                            vaultContents.map((item) => (
                              <div key={item.id} className="vault-content-item">
                                <div className="vault-content-info">
                                  <span className="vault-content-type">{item.type}</span>
                                  <h4>{item.title}</h4>
                                  {item.type === 'text' && item.content && (
                                    <p className="vault-text-preview">{item.content.substring(0, 50)}{item.content.length > 50 ? '...' : ''}</p>
                                  )}
                                </div>
                                <div className="vault-item-actions">
                                  {item.type !== 'text' && item.content && (
                                    <button
                                      className="view-vault-content-btn"
                                      onClick={() => {
                                        if (item.type === 'image') {
                                          setExpandedImage(item.content);
                                        } else if (item.type === 'video') {
                                          setExpandedVideo(item.content);
                                        } else if (item.type === 'audio') {
                                          setExpandedAudio(item.content);
                                        }
                                      }}
                                    >
                                      View
                                    </button>
                                  )}
                                  <button
                                    className="delete-vault-content-btn"
                                    onClick={() => handleDeleteVaultContent(item.id, item.storagePath)}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </>
                    )}

                    {/* Omi's View - Unlock vault or view rewards */}
                    {userProfile?.name === 'Omi' && (
                      <>
                        <div className="reward-vault-header">
                          <h2>{favorMeter >= 100 ? 'Vault Unlocked!' : 'Reward Vault'}</h2>
                          <div className="vault-progress">
                            <span className="progress-value">{favorMeter}/100</span>
                          </div>
                        </div>

                        {favorMeter < 100 ? (
                          <div className="vault-locked-view">
                            <div className="locked-icon">
                              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                                <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <circle cx="12" cy="16" r="1" fill="currentColor"/>
                              </svg>
                            </div>
                            <p className="vault-locked-message">
                              {vaultContents.length > 0
                                ? `${vaultContents.length} reward${vaultContents.length > 1 ? 's' : ''} waiting for you...`
                                : 'The vault is empty for now...'}
                            </p>
                            <p className="vault-progress-message">
                              Earn {100 - favorMeter} more favor points to unlock!
                            </p>
                          </div>
                        ) : (
                          <div className="vault-unlockable-view">
                            {vaultContents.length > 0 ? (
                              <>
                                <div className="unlocked-icon">
                                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
                                    <path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.0503 2 15.8124 3.2341 16.584 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                    <circle cx="12" cy="16" r="1" fill="currentColor"/>
                                  </svg>
                                </div>
                                <p className="vault-ready-message">
                                  {vaultContents.length} reward{vaultContents.length > 1 ? 's' : ''} ready to claim!
                                </p>
                                <button
                                  className="unlock-vault-btn"
                                  onClick={handleUnlockVault}
                                >
                                  Unlock Vault
                                </button>
                                <p className="vault-warning">This will reset your favor meter to 0</p>
                              </>
                            ) : (
                              <div className="vault-empty-message">
                                <p>The vault is empty right now.</p>
                                <p className="vault-hint">Henry hasn't added any rewards yet...</p>
                              </div>
                            )}
                          </div>
                        )}

                      </>
                    )}

                    <button
                      className="close-vault-btn"
                      onClick={() => {
                        setShowRewardVault(false);
                        setShowAddVaultContent(false);
                      }}
                    >
                      Close
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Protocol Editor Modal - Henry Only */}
            <AnimatePresence>
              {showProtocolEditor && userProfile?.name === 'Henry' && (
                <motion.div
                  className="protocol-editor-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowProtocolEditor(false)}
                >
                  <motion.div
                    className="protocol-editor-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2>Edit Protocols</h2>
                    <p className="protocol-editor-description">
                      These protocols must be accepted by Omi each day before accessing the dashboard.
                    </p>

                    <div className="protocol-list-editor">
                      {editingProtocols.map((protocol, index) => (
                        <div key={protocol.id || index} className="protocol-edit-item">
                          <span className="protocol-number">{index + 1}.</span>
                          <input
                            type="text"
                            value={protocol.text}
                            onChange={(e) => handleProtocolTextChange(index, e.target.value)}
                            placeholder="Enter protocol text..."
                          />
                          <button
                            className="remove-protocol-btn"
                            onClick={() => handleRemoveProtocol(index)}
                            disabled={editingProtocols.length <= 1}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>

                    <button className="add-protocol-btn" onClick={handleAddProtocol}>
                      + Add Protocol
                    </button>

                    <div className="protocol-editor-actions">
                      <button
                        className="cancel-protocol-btn"
                        onClick={() => setShowProtocolEditor(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="save-protocol-btn"
                        onClick={handleSaveProtocols}
                        disabled={editingProtocols.filter(p => p.text.trim()).length === 0}
                      >
                        Save Protocols
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Henry's Gifts Modal - Omi Only (separate from vault) */}
            <AnimatePresence>
              {showRewardStorage && userProfile?.name === 'Omi' && (
                <motion.div
                  className="reward-storage-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => {
                    setShowRewardStorage(false);
                    setExpandedReward(null);
                  }}
                >
                  <motion.div
                    className="reward-storage-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="reward-storage-header">
                      <h2>Henry's Gifts</h2>
                      <button
                        className="close-modal-btn"
                        onClick={() => {
                          setShowRewardStorage(false);
                          setExpandedReward(null);
                        }}
                      >
                        x
                      </button>
                    </div>

                    <div className="reward-storage-grid">
                      {rewardStorage.length === 0 ? (
                        <p className="no-rewards-message">No rewards unlocked yet...</p>
                      ) : (
                        rewardStorage.map((item) => (
                          <motion.div
                            key={item.id}
                            className="reward-storage-card"
                            whileHover={{ scale: 1.02 }}
                            onClick={() => setExpandedReward(item)}
                          >
                            <div className="reward-card-preview">
                              {item.type === 'text' && (
                                <div className="reward-text-preview">
                                  <p>{item.content.substring(0, 100)}...</p>
                                </div>
                              )}
                              {item.type === 'image' && (
                                <img src={item.content} alt={item.title} />
                              )}
                              {item.type === 'video' && (
                                <video src={item.content} />
                              )}
                              {item.type === 'audio' && (
                                <div className="reward-audio-preview">
                                  <span>Audio</span>
                                </div>
                              )}
                            </div>
                            <div className="reward-card-info">
                              <h4>{item.title}</h4>
                              <p>{new Date(item.unlockedAt).toLocaleDateString()}</p>
                            </div>
                          </motion.div>
                        ))
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Punishment Popup Modal - Omi Only */}
            <AnimatePresence>
              {showPunishmentPopup && activePunishment && userProfile?.name === 'Omi' && (
                <motion.div
                  className="punishment-popup-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <motion.div
                    className="punishment-popup-modal"
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.8, y: 20 }}
                  >
                    <div className="punishment-popup-icon">
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h2>You Are Being Punished</h2>
                    <p className="punishment-disappointment">
                      Henry is disappointed in your submission. Your favor has fallen below acceptable levels.
                    </p>
                    <div className="punishment-details">
                      <h3>Your Punishment:</h3>
                      <p className="punishment-text">{activePunishment.text}</p>
                    </div>
                    <p className="punishment-notice">
                      Your rewards are locked until this punishment is complete. Complete your punishment and earn Henry's forgiveness.
                    </p>
                    <button
                      className="punishment-acknowledge-btn"
                      onClick={() => {
                        setShowPunishmentPopup(false);
                        setPunishmentPopupDismissed(true);
                        sessionStorage.setItem('punishmentPopupDismissed', 'true');
                      }}
                    >
                      I Understand
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Punishment Manager Modal - Henry Only */}
            <AnimatePresence>
              {showPunishmentManager && userProfile?.name === 'Henry' && (
                <motion.div
                  className="punishment-manager-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowPunishmentManager(false)}
                >
                  <motion.div
                    className="punishment-manager-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="punishment-manager-header">
                      <h2>Punishment Manager</h2>
                      <button
                        className="close-modal-btn"
                        onClick={() => setShowPunishmentManager(false)}
                      >
                        x
                      </button>
                    </div>

                    {/* Active Punishment Section */}
                    {activePunishment && (
                      <div className="active-punishment-section">
                        <h3>Active Punishment</h3>
                        <div className="active-punishment-card">
                          <p className="active-punishment-text">{activePunishment.text}</p>
                          <p className="active-punishment-date">
                            Activated: {new Date(activePunishment.activatedAt).toLocaleDateString()}
                          </p>
                          <button
                            className="complete-punishment-btn"
                            onClick={handleCompletePunishment}
                          >
                            Mark Punishment Complete
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Add New Punishment */}
                    <div className="add-punishment-section">
                      <h3>Add Punishment to Pool</h3>
                      <div className="add-punishment-form">
                        <textarea
                          value={newPunishmentText}
                          onChange={(e) => setNewPunishmentText(e.target.value)}
                          placeholder="Describe the punishment..."
                          rows="3"
                        />
                        <button
                          className="add-punishment-btn"
                          onClick={handleAddPunishment}
                          disabled={!newPunishmentText.trim()}
                        >
                          Add Punishment
                        </button>
                      </div>
                    </div>

                    {/* Punishment Pool List */}
                    <div className="punishment-pool-section">
                      <h3>Punishment Pool ({punishments.length})</h3>
                      {punishments.length === 0 ? (
                        <p className="no-punishments">No punishments in pool. Add some above.</p>
                      ) : (
                        <div className="punishment-pool-list">
                          {punishments.map((punishment) => (
                            <div key={punishment.id} className="punishment-pool-item">
                              <p>{punishment.text}</p>
                              <button
                                className="delete-punishment-btn"
                                onClick={() => handleDeletePunishment(punishment.id)}
                              >
                                Delete
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Expanded Reward View */}
            <AnimatePresence>
              {expandedReward && (
                <motion.div
                  className="expanded-reward-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setExpandedReward(null)}
                >
                  <motion.div
                    className="expanded-reward-content"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="close-expanded-btn"
                      onClick={() => setExpandedReward(null)}
                    >
                      x
                    </button>
                    <h3>{expandedReward.title}</h3>
                    {expandedReward.type === 'text' && (
                      <p className="expanded-text-content">{expandedReward.content}</p>
                    )}
                    {expandedReward.type === 'image' && (
                      <img src={expandedReward.content} alt={expandedReward.title} className="expanded-media" />
                    )}
                    {expandedReward.type === 'video' && (
                      <video src={expandedReward.content} controls className="expanded-media" />
                    )}
                    {expandedReward.type === 'audio' && (
                      <audio src={expandedReward.content} controls className="expanded-audio" />
                    )}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tribute Archive Modal - Henry Only */}
            <AnimatePresence>
              {showTributeArchive && userProfile?.name === 'Henry' && (
                <motion.div
                  className="tribute-archive-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowTributeArchive(false)}
                >
                  <motion.div
                    className="tribute-archive-modal"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="tribute-archive-header">
                      <h2>Tribute Archive</h2>
                      <div className="tribute-header-actions">
                        {tributes.length > 0 && (
                          <button
                            className="download-all-btn"
                            onClick={handleDownloadAllTributes}
                          >
                            Download All ({tributes.length})
                          </button>
                        )}
                        <button
                          className="close-modal-btn"
                          onClick={() => setShowTributeArchive(false)}
                        >
                          x
                        </button>
                      </div>
                    </div>

                    <div className="tribute-archive-content">
                      {tributes.length === 0 ? (
                        <p className="no-tributes">No tributes yet...</p>
                      ) : (
                        <div className="tributes-grid">
                          {tributes.map((tribute) => (
                            <motion.div
                              key={tribute.id}
                              className="tribute-card"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              onClick={() => setExpandedTribute(tribute)}
                              whileHover={{ scale: 1.02 }}
                            >
                              <div className="tribute-media">
                                {tribute.mediaType === 'image' && tribute.mediaUrl && (
                                  <img src={tribute.mediaUrl} alt={tribute.taskTitle} />
                                )}
                                {tribute.mediaType === 'video' && tribute.mediaUrl && (
                                  <div className="tribute-video-preview">
                                    <video src={tribute.mediaUrl} />
                                    <div className="video-play-icon">▶</div>
                                  </div>
                                )}
                                {tribute.mediaType === 'audio' && (
                                  <div className="tribute-audio-preview">
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M9 18V5L21 3V16M9 18C9 19.6569 7.20914 21 5 21C2.79086 21 1 19.6569 1 18C1 16.3431 2.79086 15 5 15C7.20914 15 9 16.3431 9 18ZM21 16C21 17.6569 19.2091 19 17 19C14.7909 19 13 17.6569 13 16C13 14.3431 14.7909 13 17 13C19.2091 13 21 14.3431 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>Audio</span>
                                  </div>
                                )}
                                {tribute.mediaType === 'text' && (
                                  <div className="tribute-text-preview">
                                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>Text</span>
                                  </div>
                                )}
                              </div>
                              <div className="tribute-info">
                                <h4>{tribute.taskTitle}</h4>
                                <p className="tribute-meta">
                                  {new Date(tribute.submittedAt).toLocaleDateString()}
                                  {tribute.isLate && <span className="late-badge">Late</span>}
                                </p>
                              </div>
                              <div className="tribute-card-actions">
                                <span className="view-hint">Click to view</span>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Expanded Tribute View */}
            <AnimatePresence>
              {expandedTribute && (
                <motion.div
                  className="expanded-tribute-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setExpandedTribute(null)}
                >
                  <motion.div
                    className="expanded-tribute-content"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      className="close-expanded-btn"
                      onClick={() => setExpandedTribute(null)}
                    >
                      x
                    </button>

                    <div className="expanded-tribute-header">
                      <h3>{expandedTribute.taskTitle}</h3>
                      <p className="expanded-tribute-meta">
                        Submitted: {new Date(expandedTribute.submittedAt).toLocaleString()}
                        {expandedTribute.isLate && <span className="late-badge">Late</span>}
                      </p>
                    </div>

                    <div className="expanded-tribute-media">
                      {expandedTribute.mediaType === 'image' && expandedTribute.mediaUrl && (
                        <img src={expandedTribute.mediaUrl} alt={expandedTribute.taskTitle} className="expanded-media" />
                      )}
                      {expandedTribute.mediaType === 'video' && expandedTribute.mediaUrl && (
                        <video src={expandedTribute.mediaUrl} controls autoPlay className="expanded-media" />
                      )}
                      {expandedTribute.mediaType === 'audio' && expandedTribute.mediaUrl && (
                        <div className="expanded-audio-container">
                          <div className="audio-visual">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9 18V5L21 3V16M9 18C9 19.6569 7.20914 21 5 21C2.79086 21 1 19.6569 1 18C1 16.3431 2.79086 15 5 15C7.20914 15 9 16.3431 9 18ZM21 16C21 17.6569 19.2091 19 17 19C14.7909 19 13 17.6569 13 16C13 14.3431 14.7909 13 17 13C19.2091 13 21 14.3431 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <audio src={expandedTribute.mediaUrl} controls autoPlay className="expanded-audio" />
                        </div>
                      )}
                      {expandedTribute.mediaType === 'text' && expandedTribute.textContent && (
                        <div className="expanded-text-container">
                          <p className="expanded-text-content">{expandedTribute.textContent}</p>
                        </div>
                      )}
                    </div>

                    <div className="expanded-tribute-actions">
                      <button
                        className="download-tribute-btn"
                        onClick={() => {
                          const link = document.createElement('a');
                          if (expandedTribute.mediaType === 'text' && expandedTribute.textContent) {
                            const blob = new Blob([expandedTribute.textContent], { type: 'text/plain' });
                            link.href = URL.createObjectURL(blob);
                            link.download = `${expandedTribute.taskTitle || 'tribute'}_${expandedTribute.id}.txt`;
                          } else if (expandedTribute.mediaUrl) {
                            link.href = expandedTribute.mediaUrl;
                            link.download = expandedTribute.fileName || `tribute_${expandedTribute.id}`;
                          }
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        Download
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        )}

        {/* ============================================
            CHOKE MODE - Breath Control Interface
            ============================================ */}
        {selectedMode === 'freaky' && selectedFreakyMode === 'breathControl' && (
          <>
            <div className="choke-mode-container">
              {/* Constriction Overlay - Visual collar effect based on favor */}
              <div
                className="constriction-overlay"
                style={{
                  '--constriction': `${constrictionLevel}%`
                }}
              />

              {/* Main Header with Breathing Ring */}
              <div className="choke-header">
                <motion.div
                  className={`breath-ring-container ${breathActive ? 'active' : ''}`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div
                    className={`breath-ring ${breathPhase} ${surpriseHold ? 'surprise' : ''}`}
                    style={{
                      '--inhale-duration': `${breathSettings.inhale}s`,
                      '--hold-duration': `${breathSettings.hold}s`,
                      '--exhale-duration': `${breathSettings.exhale}s`
                    }}
                  >
                    <div className="breath-ring-inner">
                      <div className="breath-ring-core">
                        {breathActive ? (
                          <>
                            <span className="breath-phase-text">
                              {surpriseHold ? 'HOLD' : breathPhase.toUpperCase()}
                            </span>
                            <span className="breath-instruction">
                              {breathPhase === 'inhale' && 'Breathe in...'}
                              {breathPhase === 'hold' && (surpriseHold ? 'Keep holding...' : 'Hold...')}
                              {breathPhase === 'exhale' && 'Breathe out...'}
                            </span>
                          </>
                        ) : (
                          <span className="breath-idle-text">Idle</span>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>

                <h1 className="choke-title">Breath Control</h1>

                {/* Gasp Button - Always visible for Omi */}
                {userProfile?.name === 'Omi' && (
                  <motion.button
                    className="gasp-button"
                    onClick={handleGasp}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="gasp-icon">💨</span>
                    <span className="gasp-label">Gasp</span>
                    {gaspCount > 0 && <span className="gasp-count">{gaspCount}</span>}
                  </motion.button>
                )}

                {/* Gasp Log Button - Henry only */}
                {userProfile?.name === 'Henry' && (
                  <motion.button
                    className="gasp-log-button"
                    onClick={() => setShowGaspLog(!showGaspLog)}
                    whileHover={{ scale: 1.05 }}
                  >
                    Gasp Log ({gaspLog.length})
                  </motion.button>
                )}
              </div>

              {/* Feature Panels */}
              <div className="choke-features-grid">

                {/* 1. BREATH CONTROLLER - Henry's control panel */}
                {userProfile?.name === 'Henry' && (
                  <motion.div
                    className="choke-feature-panel breath-controller-panel"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <h3>Breath Controller</h3>
                    <p className="panel-description">Control her breathing rhythm</p>

                    <div className="breath-sliders">
                      <div className="slider-group">
                        <label>Inhale: {breathSettings.inhale}s</label>
                        <input
                          type="range"
                          min="2"
                          max="10"
                          value={breathSettings.inhale}
                          onChange={(e) => handleUpdateBreathSettings({
                            ...breathSettings,
                            inhale: parseInt(e.target.value)
                          })}
                        />
                      </div>
                      <div className="slider-group">
                        <label>Hold: {breathSettings.hold}s</label>
                        <input
                          type="range"
                          min="0"
                          max="15"
                          value={breathSettings.hold}
                          onChange={(e) => handleUpdateBreathSettings({
                            ...breathSettings,
                            hold: parseInt(e.target.value)
                          })}
                        />
                      </div>
                      <div className="slider-group">
                        <label>Exhale: {breathSettings.exhale}s</label>
                        <input
                          type="range"
                          min="2"
                          max="10"
                          value={breathSettings.exhale}
                          onChange={(e) => handleUpdateBreathSettings({
                            ...breathSettings,
                            exhale: parseInt(e.target.value)
                          })}
                        />
                      </div>
                    </div>

                    <div className="breath-controls">
                      <button
                        className={`breath-toggle-btn ${breathActive ? 'active' : ''}`}
                        onClick={handleToggleBreathControl}
                      >
                        {breathActive ? 'Stop Control' : 'Start Control'}
                      </button>
                      {breathActive && (
                        <button
                          className="surprise-hold-btn"
                          onClick={handleSurpriseHold}
                        >
                          Surprise Hold
                        </button>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* 2. PERMISSION TO BREATHE */}
                <motion.div
                  className="choke-feature-panel permission-panel"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <h3>Permission to Breathe</h3>

                  {userProfile?.name === 'Omi' ? (
                    <>
                      <p className="panel-description">Request permission</p>
                      <div className="permission-request-area">
                        {!breathRequest && (
                          <motion.button
                            className="request-breath-btn"
                            onClick={handleRequestBreath}
                            onMouseDown={() => {}}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <span className="hold-text">Hold to Request</span>
                          </motion.button>
                        )}
                        {breathRequest?.status === 'pending' && (
                          <div className="request-pending">
                            <div className="pending-spinner"></div>
                            <span>Awaiting permission...</span>
                          </div>
                        )}
                        {breathRequest?.status === 'granted' && (
                          <motion.div
                            className="request-granted"
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                          >
                            <span className="granted-icon">✓</span>
                            <span>Breathe.</span>
                          </motion.div>
                        )}
                        {breathRequest?.status === 'denied' && (
                          <motion.div
                            className="request-denied"
                            initial={{ scale: 0.8 }}
                            animate={{ scale: 1 }}
                          >
                            <span className="denied-icon">✕</span>
                            <span>Denied.</span>
                          </motion.div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="panel-description">Grant or deny</p>
                      {partnerBreathRequest?.status === 'pending' ? (
                        <div className="permission-response-area">
                          <motion.div
                            className="partner-requesting"
                            animate={{ opacity: [0.5, 1, 0.5] }}
                            transition={{ repeat: Infinity, duration: 1.5 }}
                          >
                            Omi is requesting to breathe...
                          </motion.div>
                          <div className="response-buttons">
                            <button
                              className="grant-btn"
                              onClick={() => handleRespondToBreathRequest(true)}
                            >
                              Grant
                            </button>
                            <button
                              className="deny-btn"
                              onClick={() => handleRespondToBreathRequest(false)}
                            >
                              Deny
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="no-request">
                          <span>No pending requests</span>
                        </div>
                      )}
                    </>
                  )}
                </motion.div>

                {/* 3. COUNTDOWN CAGE */}
                <motion.div
                  className="choke-feature-panel cage-panel"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3>The Cage</h3>
                  <p className="panel-description">Hold your breath challenge</p>

                  {!cageActive ? (
                    userProfile?.name === 'Omi' ? (
                      <div className="cage-setup">
                        <div className="cage-duration-select">
                          <label>Duration:</label>
                          <select
                            value={cageMaxTime}
                            onChange={(e) => setCageMaxTime(parseInt(e.target.value))}
                          >
                            <option value={15}>15 seconds</option>
                            <option value={30}>30 seconds</option>
                            <option value={45}>45 seconds</option>
                            <option value={60}>60 seconds</option>
                            <option value={90}>90 seconds</option>
                          </select>
                        </div>
                        <button
                          className="start-cage-btn"
                          onClick={() => handleStartCage(cageMaxTime)}
                        >
                          Enter the Cage
                        </button>
                      </div>
                    ) : (
                      <div className="cage-waiting">
                        <span>Waiting for Omi to enter...</span>
                      </div>
                    )
                  ) : (
                    <div className="cage-active">
                      <div className="cage-visual">
                        <div className="cage-bars">
                          {[...Array(8)].map((_, i) => (
                            <div
                              key={i}
                              className="cage-bar"
                              style={{
                                '--bar-index': i,
                                '--progress': `${(cageTimer / cageMaxTime) * 100}%`
                              }}
                            />
                          ))}
                        </div>
                        <div className="cage-timer">
                          <span className="timer-value">{cageMaxTime - cageTimer}</span>
                          <span className="timer-label">seconds</span>
                        </div>
                      </div>

                      {userProfile?.name === 'Omi' && (
                        <button
                          className="safe-signal-btn"
                          onClick={handleSafeSignal}
                        >
                          🛑 Safe Signal
                        </button>
                      )}

                      {userProfile?.name === 'Henry' && (
                        <button
                          className="release-cage-btn"
                          onClick={handleReleaseCage}
                        >
                          Release Early
                        </button>
                      )}
                    </div>
                  )}
                </motion.div>

                {/* 4. WHISPERED COMMANDS */}
                <motion.div
                  className="choke-feature-panel whisper-panel"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h3>Whispered Commands</h3>
                  <p className="panel-description">
                    {userProfile?.name === 'Henry' ? 'Record commands' : 'Listen and obey'}
                  </p>

                  {userProfile?.name === 'Henry' ? (
                    <div className="whisper-controls">
                      <div className="whisper-record">
                        {!isRecordingWhisper ? (
                          <button
                            className="start-record-btn"
                            onClick={handleStartWhisperRecording}
                          >
                            🎤 Record Command
                          </button>
                        ) : (
                          <button
                            className="stop-record-btn recording"
                            onClick={handleStopWhisperRecording}
                          >
                            ⏹ Stop Recording
                          </button>
                        )}
                      </div>

                      <div className="whisper-list">
                        <h4>Saved Commands ({chokeWhispers.length})</h4>
                        {chokeWhispers.map((whisper) => (
                          <div key={whisper.id} className="whisper-item">
                            <audio src={whisper.audio} controls />
                            <button
                              className="delete-whisper-btn"
                              onClick={() => handleDeleteWhisper(whisper.id)}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="whisper-listen">
                      {playingCommand ? (
                        <motion.div
                          className="command-playing"
                          animate={{ scale: [1, 1.05, 1] }}
                          transition={{ repeat: Infinity, duration: 0.5 }}
                        >
                          <span className="playing-icon">🔊</span>
                          <span>Listen...</span>
                        </motion.div>
                      ) : (
                        <>
                          <button
                            className="play-random-btn"
                            onClick={handlePlayRandomWhisper}
                            disabled={chokeWhispers.length === 0}
                          >
                            Play Random Command
                          </button>
                          {chokeWhispers.length === 0 && (
                            <span className="no-commands">No commands recorded yet</span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </motion.div>

                {/* 5. BREATH SYNC CHALLENGE */}
                <motion.div
                  className="choke-feature-panel sync-panel"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <h3>Breath Sync</h3>
                  <p className="panel-description">Breathe together</p>

                  {!syncActive ? (
                    <button
                      className="start-sync-btn"
                      onClick={handleStartSyncChallenge}
                    >
                      Start Sync Challenge
                    </button>
                  ) : (
                    <div className="sync-active">
                      <div className="sync-visual">
                        <motion.div
                          className="sync-ring"
                          animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.5, 1, 0.5]
                          }}
                          transition={{ repeat: Infinity, duration: 4 }}
                        />
                        <motion.button
                          className="sync-tap-btn"
                          onClick={handleSyncTap}
                          whileTap={{ scale: 0.9 }}
                        >
                          TAP
                        </motion.button>
                      </div>
                      <div className="sync-score">
                        <span className="score-value">{syncScore}</span>
                        <span className="score-label">Sync Score</span>
                      </div>
                      <button
                        className="stop-sync-btn"
                        onClick={handleStopSyncChallenge}
                      >
                        End Challenge
                      </button>
                    </div>
                  )}
                </motion.div>

                {/* 6. EDGE TIMER */}
                <motion.div
                  className="choke-feature-panel edge-panel"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <h3>Edge Timer</h3>
                  <p className="panel-description">
                    {userProfile?.name === 'Henry' ? 'Set secret duration' : 'Endure the unknown'}
                  </p>

                  {userProfile?.name === 'Henry' ? (
                    !edgeActive ? (
                      <div className="edge-setup">
                        <div className="edge-duration">
                          <label>Secret Duration (seconds):</label>
                          <input
                            type="number"
                            min="30"
                            max="300"
                            value={edgeSecretDuration}
                            onChange={(e) => setEdgeSecretDuration(parseInt(e.target.value))}
                          />
                        </div>
                        <div className="edge-reward">
                          <label>Reward Message:</label>
                          <input
                            type="text"
                            placeholder="Message when completed..."
                            value={edgeRewardMessage}
                            onChange={(e) => setEdgeRewardMessage(e.target.value)}
                          />
                        </div>
                        <button
                          className="start-edge-btn"
                          onClick={() => handleStartEdgeTimer(edgeSecretDuration, edgeRewardMessage)}
                        >
                          Start Timer
                        </button>
                      </div>
                    ) : (
                      <div className="edge-monitoring">
                        <div className="edge-status">
                          <span>Timer Active</span>
                          <span className="edge-remaining">
                            {Math.max(0, Math.floor(edgeSecretDuration - ((Date.now() - edgeStartTime) / 1000)))}s remaining
                          </span>
                        </div>
                        <button
                          className="reset-edge-btn"
                          onClick={handleResetEdge}
                        >
                          Reset Timer
                        </button>
                      </div>
                    )
                  ) : (
                    <div className="edge-endure">
                      {!edgeActive ? (
                        <span className="edge-waiting">Waiting for timer...</span>
                      ) : edgeCompleted ? (
                        <motion.div
                          className="edge-completed"
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                        >
                          <span className="completed-icon">✨</span>
                          <span className="reward-message">{edgeRewardMessage || 'Good girl.'}</span>
                        </motion.div>
                      ) : (
                        <div className="edge-progress-container">
                          <div className="edge-unknown-progress">
                            <motion.div
                              className="edge-progress-ring"
                              style={{
                                background: `conic-gradient(
                                  rgba(139, 69, 69, 0.8) ${edgeProgress * 3.6}deg,
                                  rgba(30, 0, 30, 0.6) ${edgeProgress * 3.6}deg
                                )`
                              }}
                            />
                            <div className="edge-center">
                              <span className="edge-question">?</span>
                            </div>
                          </div>
                          <span className="edge-endure-text">Endure...</span>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              </div>

              {/* Gasp Log Modal - Henry only */}
              <AnimatePresence>
                {showGaspLog && userProfile?.name === 'Henry' && (
                  <motion.div
                    className="gasp-log-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowGaspLog(false)}
                  >
                    <motion.div
                      className="gasp-log-modal"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="gasp-log-header">
                        <h2>Gasp Log</h2>
                        <button
                          className="close-modal-btn"
                          onClick={() => setShowGaspLog(false)}
                        >
                          ✕
                        </button>
                      </div>

                      <div className="gasp-log-content">
                        {gaspLog.length === 0 ? (
                          <p className="no-gasps">No gasps recorded</p>
                        ) : (
                          <>
                            <div className="gasp-summary">
                              <span>Total Gasps: {gaspLog.length}</span>
                              <span>Today: {gaspCount}</span>
                            </div>
                            <div className="gasp-list">
                              {gaspLog.map((gasp) => (
                                <div key={gasp.id} className="gasp-entry">
                                  <span className="gasp-time">
                                    {new Date(gasp.timestamp).toLocaleString()}
                                  </span>
                                </div>
                              ))}
                            </div>
                            <button
                              className="clear-gasp-log-btn"
                              onClick={handleClearGaspLog}
                            >
                              Clear Log
                            </button>
                          </>
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Cage Timer Effect */}
              {cageActive && (
                <motion.div
                  className="cage-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    '--cage-progress': `${(cageTimer / cageMaxTime) * 100}%`
                  }}
                />
              )}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default Intimacy;
